import React, { useState, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, increment, or, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Chessboard } from 'react-chessboard';
import ChessBoard from '../components/ChessBoard';

import LibraryManager from '../components/LibraryManager';
import CoachHome from '../components/CoachTabs/CoachHome';
import CoachDashboardHome from '../components/CoachTabs/CoachDashboardHome';
import CoachHistory from '../components/CoachTabs/CoachHistory';
import CoachCurriculum from '../components/CoachTabs/CoachCurriculum';
import CoachPerformance from '../components/CoachTabs/CoachPerformance';
import StudentActionModal from '../components/CoachTabs/StudentActionModal';
import ArcadePanel from '../components/Arcade/ArcadePanel';
import iconPawn3d from '../assets/3d_icons/icon_pawn.png';
import ArcadeMonitor from '../components/Arcade/ArcadeMonitor';
import { PIECES as HUNGRY_PIECES, HUNGRY_CHAPTERS } from '../components/HungryPieceGame';
import ProfileSettings from '../components/ProfileSettings';
import LiveVideo from '../components/LiveVideo';
import { CartoonAvatar } from '../components/GamificationAssets';
import SkeletonLoader from '../components/SkeletonLoader';
import { PIECE_IMAGES, BOTS } from '../utils/AppConstants';
import CurriculumViewer from '../components/CoachTabs/CurriculumViewer';
import LichessNotationTree from '../components/LichessNotationTree';
import { convertLegacyToTree } from '../utils/ChessGameTree';

const MiniPreviewBoard = React.memo(({ fen }) => {
    const temp = new Chess(); try { temp.load(fen); } catch(e){}
    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    const render = {}; temp.board().forEach((row, r) => row.forEach((sq, c) => { if(sq) render[`${cols[c]}${rows[r]}`] = sq.color + sq.type.toUpperCase(); }));
    return (
        <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '6px', overflow: 'hidden', boxShadow: 'var(--neu-shadow-inset)' }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                return ( <div key={id} style={{background: isDark ? '#8ca2ad' : '#dee3e6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}> {render[id] && <img src={PIECE_IMAGES[render[id]]} alt="p" style={{width: '90%' }} />} </div> );
            }))}
        </div>
    );
}, (prev, next) => prev.fen === next.fen);

const MiniBasicsBoard = React.memo(({ piece, piecePos, apples = [] }) => {
    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    return (
        <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '1px solid #f59e0b', borderRadius: '6px', overflow: 'hidden', boxShadow: 'var(--neu-shadow-inset)' }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const sq = `${col}${row}`;
                const isDark = (rIdx + cIdx) % 2 === 1;
                const hasPiece = piecePos === sq;
                const hasApple = apples.includes(sq);
                return (
                    <div key={sq} style={{background: isDark ? '#8ca2ad' : '#dee3e6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {hasPiece && <img src={HUNGRY_PIECES[piece] || HUNGRY_PIECES['Rook']} alt="p" style={{width: '90%' }} />}
                        {hasApple && !hasPiece && <span style={{fontSize: '14px', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }}>🍎</span>}
                    </div>
                );
            }))}
        </div>
    );
}, (prev, next) => prev.piece === next.piece && prev.piecePos === next.piecePos && JSON.stringify(prev.apples) === JSON.stringify(next.apples));

const LaunchQuizModal = ({ show, onClose, onConfirm, currentFen }) => {
    const [question, setQuestion] = useState('Find the best move');
    const [solutionMove, setSolutionMove] = useState('');
    const [points, setPoints] = useState(10);
    const [timeLimit, setTimeLimit] = useState(60);
    const [playedMoves, setPlayedMoves] = useState([]);
    const [quizGame, setQuizGame] = useState(() => {
        const initFen = currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        try { return new Chess(initFen); } catch (e) { return new Chess(); }
    });

    useEffect(() => {
        if (show) {
            const initFen = currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
            try { setQuizGame(new Chess(initFen)); } catch (e) { setQuizGame(new Chess()); }
            setQuestion('Find the best move');
            setSolutionMove('');
            setPlayedMoves([]);
            setPoints(10);
            setTimeLimit(60);
        }
    }, [show, currentFen]);

    if (!show) return null;

    const handlePieceDrop = (sourceSquare, targetSquare) => {
        try {
            const temp = new Chess(quizGame.fen());
            const move = temp.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
            if (move) {
                setQuizGame(temp);
                const updated = [...playedMoves, move.san];
                setPlayedMoves(updated);
                setSolutionMove(updated.join(', '));
                return true;
            }
        } catch (err) {}
        return false;
    };

    const handleResetMoves = () => {
        const initFen = currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        try { setQuizGame(new Chess(initFen)); } catch (e) { setQuizGame(new Chess()); }
        setPlayedMoves([]);
        setSolutionMove('');
    };

    const handleConfirm = () => {
        if (!solutionMove.trim()) {
            toast.error("Please enter or play the solution move (e.g. e4, Nf3, or e2e4)!");
            return;
        }
        onConfirm({
            question: question.trim() || 'Find the best move',
            answer: solutionMove.trim(),
            points: Number(points) || 10,
            timeLimit: Number(timeLimit) || 60
        });
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(15, 23, 42, 0.82)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            boxSizing: 'border-box'
        }}>
            <div style={{
                background: 'var(--bg-primary)',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '520px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 35px rgba(16, 185, 129, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Modal Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                    padding: '20px 24px',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>🎯</span>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '900' }}>Launch Position as Live Quiz</h2>
                            <div style={{ fontSize: '11px', opacity: 0.9 }}>Play moves on the board below to set the solution sequence!</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
                    >
                        ✕
                    </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '80vh', overflowY: 'auto' }}>
                    {/* Playable Interactive Board & Instructions */}
                    <div style={{ display: 'flex', gap: '16px', background: 'var(--bg-secondary)', padding: '14px', borderRadius: '16px', border: '1px solid rgba(16, 185, 129, 0.3)', alignItems: 'center' }}>
                        <div style={{ width: '160px', height: '160px', flexShrink: 0, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            <Chessboard 
                                position={quizGame.fen()} 
                                onPieceDrop={handlePieceDrop} 
                                boardWidth={160} 
                                customBoardStyle={{ borderRadius: '10px' }}
                            />
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>🎮</span> Play Moves to Set Answer
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                                Drag & drop pieces on the mini board to play move(s). Played moves automatically populate the solution!
                            </div>
                            {playedMoves.length > 0 && (
                                <div style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '6px 10px', borderRadius: '6px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                                    Played: {playedMoves.join(', ')}
                                </div>
                            )}
                            <button
                                onClick={handleResetMoves}
                                style={{
                                    alignSelf: 'flex-start',
                                    padding: '4px 10px',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#ef4444',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                🔄 Reset Moves
                            </button>
                        </div>
                    </div>

                    {/* Question Field */}
                    <div>
                        <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                            ❓ Quiz Prompt / Question
                        </label>
                        <input
                            type="text"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="e.g. Find the best move for White"
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                border: '1px solid rgba(163, 177, 198, 0.4)',
                                borderRadius: '10px',
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    {/* Solution Move Field */}
                    <div>
                        <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                            ✅ Solution Move Sequence (Required)
                        </label>
                        <input
                            type="text"
                            value={solutionMove}
                            onChange={(e) => setSolutionMove(e.target.value)}
                            placeholder="e.g. e4, Nf3 or play on board above"
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                border: '2px solid #10b981',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                            Accepts SAN (e.g. Nf3, e4), coordinates, or multi-move sequences separated by commas.
                        </div>
                    </div>

                    {/* Points & Time Controls Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                                🏆 Reward Points (XP)
                            </label>
                            <select
                                value={points}
                                onChange={(e) => setPoints(Number(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid rgba(163, 177, 198, 0.4)',
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    outline: 'none'
                                }}
                            >
                                <option value={5}>5 XP</option>
                                <option value={10}>10 XP (Default)</option>
                                <option value={15}>15 XP</option>
                                <option value={20}>20 XP</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                                ⏱️ Time Limit
                            </label>
                            <select
                                value={timeLimit}
                                onChange={(e) => setTimeLimit(Number(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid rgba(163, 177, 198, 0.4)',
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    outline: 'none'
                                }}
                            >
                                <option value={30}>30 Seconds</option>
                                <option value={60}>60 Seconds (Default)</option>
                                <option value={90}>90 Seconds</option>
                                <option value={120}>2 Minutes</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div style={{
                    padding: '16px 24px',
                    background: 'var(--bg-secondary)',
                    borderTop: '1px solid rgba(163, 177, 198, 0.3)',
                    display: 'flex',
                    gap: '12px'
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: '12px',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            border: '1px solid rgba(163, 177, 198, 0.5)',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '13px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        style={{
                            flex: 2,
                            padding: '12px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '13px',
                            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                        }}
                    >
                        🚀 Launch Live Quiz
                    </button>
                </div>
            </div>
        </div>
    );
};

const EndClassModal = ({ show, onClose, onConfirm, classesList, classId, usersMap }) => {
    const [notes, setNotes] = useState('');
    const [attendanceData, setAttendanceData] = useState({});

    useEffect(() => {
        if (show && classId) {
            setNotes('');
            const cls = classesList.find(c => c.id === classId);
            const initialAtt = {};
            const presence = cls?.presence || {};
            (cls?.students || []).forEach(email => {
                const safeEmail = email.replace(/\./g, '_');
                // Auto-mark present if student was online in class
                const isOnline = presence[safeEmail] && (Date.now() - presence[safeEmail] < 300000);
                initialAtt[safeEmail] = isOnline ? true : false;
            });
            setAttendanceData(initialAtt);
        }
    }, [show, classId, classesList]);

    if (!show) return null;
    const cls = classesList.find(c => c.id === classId);
    const enrolledStudents = cls?.students || [];

    const handleConfirm = () => onConfirm(classId, notes, attendanceData);

    const markAll = (status) => {
        const updated = { ...attendanceData };
        enrolledStudents.forEach(email => {
            updated[email.replace(/\./g, '_')] = status;
        });
        setAttendanceData(updated);
    };

    const durationMins = cls?.startedAt ? Math.max(1, Math.round((Date.now() - cls.startedAt) / 60000)) : null;
    const movesCount = cls?.moveHistory?.length || 0;
    const presentCount = Object.values(attendanceData).filter(Boolean).length;

    const summaryPresets = [
        "Covered Italian Opening & Traps ♟️",
        "Practiced Tactics & Checkmate Patterns 🧩",
        "Endgame Fundamentals & Pawn Structure 👑",
        "Live Student Pair Matches & Analysis ⚔️"
    ];

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(15, 23, 42, 0.82)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            boxSizing: 'border-box'
        }}>
            <div style={{
                background: 'var(--bg-primary)',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '540px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(239, 68, 68, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                maxHeight: '90vh'
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                    padding: '20px 24px',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>🏁</span> End Class Summary
                        </h2>
                        <button 
                            onClick={onClose} 
                            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
                        >
                            ✕
                        </button>
                    </div>
                    <div style={{ fontSize: '13px', opacity: 0.9 }}>
                        {cls?.topic || 'Chess Lesson'} {cls?.batchName ? `• ${cls.batchName}` : ''}
                    </div>

                    {/* Stats Badges */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                        {durationMins !== null && (
                            <span style={{ background: 'rgba(0,0,0,0.25)', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                                ⏱️ {durationMins} mins live
                            </span>
                        )}
                        <span style={{ background: 'rgba(0,0,0,0.25)', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                            ♟️ {movesCount} moves played
                        </span>
                        <span style={{ background: 'rgba(0,0,0,0.25)', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                            👥 {presentCount} / {enrolledStudents.length} present
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    
                    {/* Class Summary Section */}
                    <div>
                        <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                            📝 Lesson Summary & Homework Notes (Optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Jot down lesson details, student progress, or topics covered today..."
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                border: '1px solid rgba(163, 177, 198, 0.4)',
                                borderRadius: '12px',
                                resize: 'none',
                                boxSizing: 'border-box',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                outline: 'none'
                            }}
                        />
                        {/* Summary Quick Presets */}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold', alignSelf: 'center' }}>Quick Presets:</span>
                            {summaryPresets.map((preset, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => setNotes(preset)}
                                    style={{
                                        padding: '3px 8px',
                                        background: 'rgba(59, 130, 246, 0.08)',
                                        border: '1px solid rgba(59, 130, 246, 0.2)',
                                        borderRadius: '8px',
                                        color: '#3b82f6',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + {preset.split(' ')[0]} {preset.split(' ')[1]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Attendance Section */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px' }}>
                                📋 Class Attendance (+50 XP for Present)
                            </label>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                    type="button"
                                    onClick={() => markAll(true)}
                                    style={{ padding: '2px 8px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid #10b981', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    ✅ All Present
                                </button>
                                <button
                                    type="button"
                                    onClick={() => markAll(false)}
                                    style={{ padding: '2px 8px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    ❌ All Absent
                                </button>
                            </div>
                        </div>

                        {/* Attendance Guide Hint */}
                        <div style={{
                            background: 'rgba(59, 130, 246, 0.08)',
                            border: '1px solid rgba(59, 130, 246, 0.25)',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            marginBottom: '10px',
                            fontSize: '11px',
                            color: 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '14px' }}>💡</span>
                            <div>
                                <strong>Attendance Guide:</strong> Green buttons mean <strong>Present</strong>. Gray/Red buttons mean <strong>Absent</strong>. Click any student's button to toggle status. Online students are auto-detected.
                            </div>
                        </div>

                        {/* Student List */}
                        <div style={{
                            background: 'var(--bg-secondary)',
                            borderRadius: '12px',
                            padding: '8px 12px',
                            maxHeight: '180px',
                            overflowY: 'auto',
                            border: '1px solid rgba(163, 177, 198, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px'
                        }}>
                            {enrolledStudents.length === 0 ? (
                                <div style={{ color: '#ef4444', fontSize: '12px', padding: '10px', textAlign: 'center' }}>
                                    No students enrolled in this class batch.
                                </div>
                            ) : (
                                enrolledStudents.map(email => {
                                    const safeEmail = email.replace(/\./g, '_');
                                    const isPresent = attendanceData[safeEmail] === true;
                                    const isOnline = cls?.presence && (Date.now() - (cls.presence[safeEmail] || 0) < 300000);

                                    return (
                                        <div
                                            key={email}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 10px',
                                                borderRadius: '8px',
                                                background: isPresent ? 'rgba(16, 185, 129, 0.06)' : 'transparent',
                                                border: isPresent ? '1px solid rgba(16, 185, 129, 0.3)' : '1px dashed rgba(163, 177, 198, 0.3)',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '14px' }}>👤</span>
                                                <div>
                                                    <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {usersMap[email] || email.split('@')[0]}
                                                        {isOnline && (
                                                            <span style={{ fontSize: '9px', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '1px 6px', borderRadius: '10px', fontWeight: 'bold' }}>
                                                                🟢 Auto-Detected Online
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => setAttendanceData({ ...attendanceData, [safeEmail]: !isPresent })}
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '8px',
                                                    border: isPresent ? 'none' : '1px solid rgba(239, 68, 68, 0.4)',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer',
                                                    background: isPresent 
                                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                                        : 'rgba(239, 68, 68, 0.1)',
                                                    color: isPresent ? 'white' : '#ef4444',
                                                    fontSize: '11px',
                                                    boxShadow: isPresent ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                title="Click to toggle status"
                                            >
                                                {isPresent ? '✅ Present (Deduct 1 Credit)' : '❌ Absent (No Credit Loss)'}
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Warning Note */}
                    <div style={{
                        color: '#f59e0b',
                        background: 'rgba(245, 158, 11, 0.08)',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>⚠️</span>
                        <div>
                            Present students lose 1 Class Credit &amp; gain +50 XP. Absent students retain their credit.
                        </div>
                    </div>
                </div>

                {/* Footer Controls */}
                <div style={{
                    padding: '16px 24px',
                    background: 'var(--bg-secondary)',
                    borderTop: '1px solid rgba(163, 177, 198, 0.3)',
                    display: 'flex',
                    gap: '12px'
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: '12px',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            border: '1px solid rgba(163, 177, 198, 0.5)',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '13px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        style={{
                            flex: 2,
                            padding: '12px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '13px',
                            boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                        }}
                    >
                        ✅ Confirm &amp; End Class
                    </button>
                </div>
            </div>
        </div>
    );
};

const SaveLessonModal = ({ show, onClose, onConfirm, defaultFolderId, foldersList }) => {
    const [title, setTitle] = useState('');
    const [targetFolder, setTargetFolder] = useState(defaultFolderId || '');

    useEffect(() => {
        if (show) {
            setTitle('');
            setTargetFolder(defaultFolderId || '');
        }
    }, [show, defaultFolderId]);

    if (!show) return null;

    const handleConfirm = () => {
        if (!title.trim()) return toast.error("Please enter a title!");
        onConfirm(title.trim(), targetFolder);
    };

    return (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{background: 'var(--bg-primary)', padding: '30px', borderRadius: '16px', width: '90%', maxWidth: '450px', border: '1px solid rgba(255,255,255,0.45)', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <h2 style={{margin: 0, color: '#3b82f6', fontSize: '20px', fontWeight: 'bold'}}>💾 Save Current Lesson</h2>
                <p style={{margin: 0, color: 'var(--text-secondary)', fontSize: '13px'}}>Save the current board position (FEN) and whiteboard notes to your Library.</p>
                
                <div>
                    <label style={{color: 'var(--text-primary)', fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '13px'}}>Lesson Title</label>
                    <input 
                        type="text" 
                        value={title} 
                        onChange={(e) => setTitle(e.target.value)} 
                        placeholder="e.g. Sicilian Defense - Dragon Intro" 
                        style={{width: '100%', padding: '10px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '8px', boxSizing: 'border-box', fontSize: '14px', outline: 'none'}} 
                    />
                </div>

                <div>
                    <label style={{color: 'var(--text-primary)', fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '13px'}}>Save Location</label>
                    <select 
                        value={targetFolder} 
                        onChange={(e) => setTargetFolder(e.target.value)} 
                        style={{width: '100%', padding: '10px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '8px', boxSizing: 'border-box', fontSize: '14px', outline: 'none'}}
                    >
                        {foldersList.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                    </select>
                </div>

                <div style={{display: 'flex', gap: '12px', marginTop: '10px'}}>
                    <button onClick={onClose} style={{flex: 1, padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: '1px solid #64748b', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer'}}>Cancel</button>
                    <button onClick={handleConfirm} style={{flex: 2, padding: '10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)'}}>Confirm & Save</button>
                </div>
            </div>
        </div>
    );
};

export default function CoachDashboard({ user, isEmbedded = false, setActiveTab }) {
    const [classesList, setClassesList] = useState([]);
    const [activeClass, setActiveClass] = useState(null);
    const [myBatches, setMyBatches] = useState([]);
    const [usersMap, setUsersMap] = useState({});
    const [allStudents, setAllStudents] = useState([]);
    const [allAcademyStudents, setAllAcademyStudents] = useState([]);
    const [myProfile, setMyProfile] = useState(null);

    const [dashTab, setDashTab] = useState('home');
    const [rightTab, setRightTab] = useState('moves');
    const [tacticTopic, setTacticTopic] = useState(null);
    const [selectedStudentAction, setSelectedStudentAction] = useState(null);

    const [liveSearch, setLiveSearch] = useState('');
    const [whiteboardInput, setWhiteboardInput] = useState('');
    const [spectatingStudentEmail, setSpectatingStudentEmail] = useState(null);
    const [isIndividualMinimized, setIsIndividualMinimized] = useState(false);
    const [spectatorCardWidth, setSpectatorCardWidth] = useState(420);
    const [spectatorCardPosition, setSpectatorCardPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [notesSyncStatus, setNotesSyncStatus] = useState('saved'); // 'saved', 'typing', 'saving', 'error'
    const [showSoloGrid, setShowSoloGrid] = useState(false);
    const editorRef = useRef(null);
    const [playingArenaGameId, setPlayingArenaGameId] = useState(null);
    const [coachFolders, setCoachFolders] = useState([]);
    const [showSaveLessonModal, setShowSaveLessonModal] = useState(false);
    
    // 🔥 DYNAMIC DRAG-TO-RESIZE PANEL STATES & HANDLERS (DOM-RESILIENT REFS)
    const [rightPanelWidth, setRightPanelWidth] = useState(480);
    const [videoHeight, setVideoHeight] = useState('240px');
    const [isDraggingRightPanel, setIsDraggingRightPanel] = useState(false);

    const rightPanelRef = useRef(null);
    const videoContainerRef = useRef(null);

    const startRightPanelResize = (e) => {
        e.preventDefault();
        setIsDraggingRightPanel(true);
        const startX = e.clientX;
        const startWidth = rightPanelWidth;
        let currentWidth = startWidth;
        const doDrag = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            currentWidth = Math.max(240, Math.min(window.innerWidth - 650, startWidth - deltaX));
            setRightPanelWidth(currentWidth);
        };
        const stopDrag = () => {
            setIsDraggingRightPanel(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const startVideoResizeFromEdge = (edge) => (e) => {
        e.preventDefault();
        const isHorizontal = edge === 'left' || edge === 'right';
        if (isHorizontal) setIsDraggingRightPanel(true);
        const startX = e.clientX || (e.touches && e.touches[0].clientX);
        const startY = e.clientY || (e.touches && e.touches[0].clientY);
        const startHeight = parseInt(videoHeight) || 240;
        const startWidth = rightPanelWidth;
        let currentHeight = startHeight;
        let currentWidth = startWidth;

        const doDrag = (moveEvent) => {
            const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
            const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
            if (currentX === undefined || currentY === undefined) return;

            if (edge === 'bottom' || edge === 'top') {
                const deltaY = edge === 'bottom' ? (currentY - startY) : (startY - currentY);
                currentHeight = Math.max(80, Math.min(600, startHeight + deltaY));
                setVideoHeight(`${currentHeight}px`);
            } else if (edge === 'left' || edge === 'right') {
                const deltaX = edge === 'left' ? (startX - currentX) : (currentX - startX);
                currentWidth = Math.max(240, Math.min(window.innerWidth - 650, startWidth + deltaX));
                setRightPanelWidth(currentWidth);
            }
        };

        const stopDrag = () => {
            if (isHorizontal) setIsDraggingRightPanel(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const startDragSpectatorCard = (e) => {
        e.preventDefault();
        setIsDragging(true);
        const startX = e.clientX || (e.touches && e.touches[0].clientX);
        const startY = e.clientY || (e.touches && e.touches[0].clientY);
        const initialX = spectatorCardPosition.x;
        const initialY = spectatorCardPosition.y;
        const doDrag = (moveEvent) => {
            const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
            const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
            if (currentX === undefined || currentY === undefined) return;
            const deltaX = currentX - startX;
            const deltaY = currentY - startY;
            setSpectatorCardPosition({
                x: initialX + deltaX,
                y: initialY + deltaY
            });
        };
        const stopDrag = () => {
            setIsDragging(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const startResizeSpectatorCard = (direction) => (e) => {
        e.preventDefault();
        setIsResizing(true);
        const startX = e.clientX || (e.touches && e.touches[0].clientX);
        const startY = e.clientY || (e.touches && e.touches[0].clientY);
        const startWidth = spectatorCardWidth;
        const doDrag = (moveEvent) => {
            const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
            const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
            if (currentX === undefined || currentY === undefined) return;
            
            let delta = 0;
            if (direction === 'left' || direction === 'top-left' || direction === 'bottom-left') {
                delta = startX - currentX;
            } else if (direction === 'right' || direction === 'top-right' || direction === 'bottom-right') {
                delta = currentX - startX;
            } else if (direction === 'top') {
                delta = startY - currentY;
            } else if (direction === 'bottom') {
                delta = currentY - startY;
            }
            
            const newWidth = Math.max(300, Math.min(800, startWidth + delta));
            setSpectatorCardWidth(newWidth);
        };
        const stopDrag = () => {
            setIsResizing(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    // insertFormat is replaced by executeStyle

    const [pairWhite, setPairWhite] = useState('');
    const [pairBlack, setPairBlack] = useState('');
    const [pairPreset, setPairPreset] = useState('5+0');
    const [pairBaseMins, setPairBaseMins] = useState(5);
    const [pairIncSecs, setPairIncSecs] = useState(0);
    const [pendingPairs, setPendingPairs] = useState([]);
    const [coachQuizViewMode, setCoachQuizViewMode] = useState('MONITOR');
    const [showLaunchQuizModal, setShowLaunchQuizModal] = useState(false);
    const [gameToCancel, setGameToCancel] = useState(null);

    const resolvePlayerName = (id) => {
        if (!id) return '';
        const norm = id.replace('bot-', 'bot_');
        if (norm.startsWith('bot_') || BOTS.some(b => b.id === norm || b.id === id)) {
            const b = BOTS.find(bot => bot.id === norm || bot.id === id);
            return b ? b.name : id;
        }
        return usersMap[id.toLowerCase()] || usersMap[id] || id.split('@')[0];
    };

    const [showEndClassModal, setShowEndClassModal] = useState(false);
    const [classToEnd, setClassToEnd] = useState(null);
    const [isEditingLink, setIsEditingLink] = useState(false);
    const [tempLink, setTempLink] = useState('');

    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();
        const unsub = onSnapshot(collection(db, "library_folders"), (snap) => {
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                if (data.owner === safeEmail || data.type === 'public' || data.parentId === 'public') {
                    list.push({ id: d.id, name: `${data.type === 'public' ? '🌍' : '📁'} ${data.name}` });
                }
            });
            setCoachFolders(list);
        });
        return () => unsub();
    }, [user]);

    useEffect(() => {
        if (spectatingStudentEmail) {
            setIsIndividualMinimized(false);
        }
        setSpectatorCardPosition({ x: 0, y: 0 });
    }, [spectatingStudentEmail]);

    useEffect(() => {
        setSpectatorCardPosition({ x: 0, y: 0 });
    }, [isIndividualMinimized]);

    // 1. Logged-in User Profile
    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();
        const unsub = onSnapshot(doc(db, "users", safeEmail), (snap) => {
            if (snap.exists()) setMyProfile({ id: snap.id, ...snap.data() });
        });
        return () => unsub();
    }, [user]);

    // 2. Users Collection (conditional based on role)
    useEffect(() => {
        if (!user?.email || !myProfile) return;
        const currentUserEmail = user.email.toLowerCase();
        const isAdmin = myProfile.role === 'OWNER' || myProfile.role === 'ADMIN';

        let qUsers;
        if (isAdmin) {
            qUsers = collection(db, "users");
        } else {
            qUsers = query(collection(db, "users"), where("role", "==", "STUDENT"));
        }

        const unsub = onSnapshot(qUsers, (snap) => {
            const map = {}; const students = []; const allAcademyStudentsList = [];
            
            // Map the current profile first
            map[currentUserEmail] = myProfile.name || myProfile.username || currentUserEmail.split('@')[0];

            snap.forEach(docSnap => {
                const data = docSnap.data(); 
                const emailKey = (data.email || docSnap.id).toLowerCase();
                map[emailKey] = data.name || data.username || emailKey.split('@')[0];
                
                if (data.role === 'STUDENT') {
                    allAcademyStudentsList.push(data);
                    if (isAdmin || data.assignedCoach === currentUserEmail) {
                        students.push(data);
                    }
                }
            });

            BOTS.forEach(b => { map[b.id] = b.name; });
            setUsersMap(map); 
            setAllStudents(students); 
            setAllAcademyStudents(allAcademyStudentsList);
        });

        return () => unsub();
    }, [user, myProfile]);

    useEffect(() => {
        if (!user?.email) return;
        const q = query(collection(db, "classes"), where("teacherId", "==", user.email.toLowerCase()));
        const unsub = onSnapshot(q, (snap) => { const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setClassesList(list); });
        return () => unsub();
    }, [user]);

    useEffect(() => {
        if (!user?.email || !myProfile) return;
        const isAdmin = myProfile.role === 'OWNER' || myProfile.role === 'ADMIN';
        const safeEmail = user.email.toLowerCase();

        let qBatches;
        if (isAdmin) {
            qBatches = collection(db, "batches");
        } else {
            qBatches = query(
                collection(db, "batches"),
                or(
                    where("coachId", "==", safeEmail),
                    where("coachEmail", "==", safeEmail)
                )
            );
        }

        const unsub = onSnapshot(qBatches, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setMyBatches(list);
        });
        return () => unsub();
    }, [user, myProfile]);

    useEffect(() => {
        const live = classesList.find(c => c.status === 'LIVE'); 
        setActiveClass(live || null);
    }, [classesList]);

    // Whiteboard Notes debounced auto-sync
    useEffect(() => {
        if (!activeClass?.id) return;
        
        // If local value matches DB, we are already synced
        const dbText = activeClass.whiteboardText || '';
        if (whiteboardInput === dbText) {
            setNotesSyncStatus('saved');
            return;
        }

        // Set typing status
        setNotesSyncStatus('typing');

        const delayDebounceFn = setTimeout(async () => {
            setNotesSyncStatus('saving');
            try {
                await updateDoc(doc(db, "classes", activeClass.id), { 
                    whiteboardText: whiteboardInput 
                });
                setNotesSyncStatus('saved');
            } catch (err) {
                console.error("Error auto-saving notes:", err);
                setNotesSyncStatus('error');
            }
        }, 1200);

        return () => clearTimeout(delayDebounceFn);
    }, [whiteboardInput, activeClass?.id]);

    // Update local input ONLY when notesSyncStatus is 'saved' to avoid overwriting coach's typing
    useEffect(() => {
        if (activeClass && activeClass.whiteboardText !== undefined) {
            if (notesSyncStatus === 'saved') {
                const dbText = activeClass.whiteboardText || '';
                setWhiteboardInput(dbText);
                if (editorRef.current && editorRef.current.innerHTML !== dbText) {
                    editorRef.current.innerHTML = dbText;
                }
            }
        }
    }, [activeClass?.whiteboardText, notesSyncStatus]);

    const executeStyle = (command, value = null) => {
        document.execCommand(command, false, value);
        if (editorRef.current) {
            setWhiteboardInput(editorRef.current.innerHTML);
            setNotesSyncStatus('typing');
        }
    };

    if (!myProfile) return <SkeletonLoader />;

    const logActivity = async (activityObj) => {
        if (!activeClass) return;
        const newLog = [...(activeClass.activityLog || []), { ...activityObj, time: Date.now() }];
        await updateDoc(doc(db, "classes", activeClass.id), { activityLog: newLog }).catch(() => {});
    };

    const handleSyncToLiveBoard = async (content, type, item = null, playlist = []) => {
        if (!activeClass) return toast.error("No active class!");
        try {
            const t = new Chess(); let newFen = ''; let newHistory = [];
            if (type === 'pgn') { t.loadPgn(content); newFen = t.fen(); newHistory = t.history(); }
            else { t.load(content); newFen = t.fen(); }
            
            const updates = { 
                currentFen: newFen, 
                moveHistory: newHistory, 
                boardHighlights: {}, 
                boardArrows: [], 
                boardItems: {}, 
                studentFens: {} 
            };
            
            if (item && item.type === 'lesson') {
                updates.whiteboardText = item.answer || '';
            }
            
            if (item && playlist.length > 0) {
                const cleanPlaylist = playlist.map(i => ({
                    id: i.id,
                    title: i.title || '',
                    type: i.type || 'position',
                    content: i.content || '',
                    answer: i.answer || '',
                    points: i.points || 10,
                    comments: i.comments || ''
                }));
                const itemIndex = playlist.findIndex(i => i.id === item.id);
                updates.playlistItems = cleanPlaylist;
                updates.playlistIndex = itemIndex >= 0 ? itemIndex : 0;
            }
            
            await updateDoc(doc(db, "classes", activeClass.id), updates);
            toast.success(item?.type === 'lesson' ? "Loaded Lesson: FEN + Whiteboard Notes! 🎓" : "Launched to Live Board! 🚀");
            logActivity({ 
                title: item?.type === 'lesson' ? `Loaded Lesson: "${item.title}"` : (type === 'pgn' ? 'Imported Game (PGN)' : 'Loaded Custom Position'), 
                fen: newFen 
            });
            if (activeClass.classMode !== 'PRACTICE') await updateDoc(doc(db, "classes", activeClass.id), { classMode: 'PRACTICE' });
        } catch (e) { 
            console.error(e);
            toast.error("Invalid format! Couldn't load."); 
        }
    };

    const changeStatus = async (id, newStatus) => {
        if (newStatus === 'COMPLETED') { setClassToEnd(id); setShowEndClassModal(true); return; }
        const data = { status: newStatus };
        if (newStatus === 'LIVE') {
            data.startedAt = Date.now();
            data.studentScores = {}; // Fresh start for new class leaderboard
            data.quizAttempts = {};
        }
        await updateDoc(doc(db, "classes", id), data);
    };

    const confirmEndClass = async (classId, notes, attendanceData) => {
        const loadingId = toast.loading("Saving History, XP & Credits...");
        try {
            const cls = classesList.find(c => c.id === classId);
            if (!cls) return toast.dismiss(loadingId);
            const data = { status: 'COMPLETED', endedAt: Date.now(), presence: attendanceData };
            if (notes && notes.trim()) data.coachStickyNote = notes.trim();
            data.activityLog = [...(cls.activityLog || []), { title: 'Class Ended', time: Date.now() }];
            await updateDoc(doc(db, "classes", classId), data);
            if (cls.batchId) { await updateDoc(doc(db, "batches", cls.batchId), { totalClassesCompleted: increment(1) }); }
            for (const safeEmail of Object.keys(attendanceData)) {
                if (attendanceData[safeEmail] === true) {
                    const actualEmail = safeEmail.replace(/_/g, '.');
                    await updateDoc(doc(db, "users", actualEmail), { classCredits: increment(-1), totalXp: increment(50) }).catch(e => console.error("Failed to update credits for", actualEmail));
                }
            }
            setShowEndClassModal(false); setClassToEnd(null);
            toast.success("Class Ended & History Saved! ✅", { id: loadingId });
        } catch (e) { toast.error("Failed to end class properly.", { id: loadingId }); }
    };

    const handleUpdateMeetLink = async () => {
        if (!activeClass) return;
        const loading = toast.loading("Updating Video Link...");
        try {
            await updateDoc(doc(db, "classes", activeClass.id), { meetLink: tempLink });
            setIsEditingLink(false);
            toast.success("Link instantly updated for all students! ⚡", { id: loading });
            logActivity({ title: 'Changed Live Video Call Link' });
        } catch (e) { toast.error("Failed to update link.", { id: loading }); }
    };

    const handleSaveLesson = async (title, folderId) => {
        if (!activeClass) return toast.error("No active class!");
        const fen = activeClass.currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const notes = activeClass.whiteboardText || '';
        const loadingId = toast.loading("Saving Lesson to Library...");
        try {
            await addDoc(collection(db, "library_items"), {
                folderId: folderId,
                title: title.trim(),
                type: 'lesson',
                content: fen,
                answer: notes,
                owner: user.email.toLowerCase(),
                createdAt: Date.now()
            });
            setShowSaveLessonModal(false);
            toast.success("Lesson saved successfully! 💾", { id: loadingId });
            logActivity({ title: `Saved Lesson: "${title.trim()}"` });
        } catch (e) {
            console.error(e);
            toast.error("Failed to save lesson.", { id: loadingId });
        }
    };

    const handleEndQuiz = async () => {
        if (!activeClass) return;
        const loadingId = toast.loading("Ending puzzle and updating XP...");
        try {
            const solvedBy = activeClass.quizData?.solvedBy || [];
            const points = activeClass.quizData?.points || 0;
            const currentScores = activeClass.studentScores || {};
            const newScores = { ...currentScores };
            
            const uniqueSolvers = [...new Set(solvedBy)];
            
            for (const email of uniqueSolvers) {
                const actualEmail = email.replace(/_/g, '.');
                const safeEmail = email.replace(/\./g, '_');
                
                newScores[safeEmail] = (newScores[safeEmail] || 0) + points;
                
                await updateDoc(doc(db, "users", actualEmail), {
                    totalXp: increment(points)
                }).catch(e => console.error("Failed to update XP for", actualEmail, e));
            }

            await updateDoc(doc(db, "classes", activeClass.id), {
                'quizData.active': false,
                classMode: 'PRACTICE',
                studentFens: {},
                quizAttempts: {},
                studentScores: newScores
            });
            setCoachQuizViewMode('MONITOR');
            toast.success("Puzzle Ended & XP added to overall profiles! 🏆", { id: loadingId });
        } catch (e) {
            console.error("Error ending quiz:", e);
            toast.error("Failed to end puzzle.", { id: loadingId });
        }
    };

    const handleLaunchCurrentAsQuiz = () => {
        if (!activeClass) return;
        setShowLaunchQuizModal(true);
    };

    const handleConfirmLaunchQuiz = async ({ question, answer, points, timeLimit }) => {
        if (!activeClass) return;
        const currentFen = activeClass.currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const loadingId = toast.loading("Launching Position as Quiz...");
        try {
            await updateDoc(doc(db, "classes", activeClass.id), {
                classMode: 'QUIZ',
                studentFens: {},
                quizAttempts: {},
                quizData: {
                    question: question,
                    content: currentFen,
                    answers: [answer],
                    endTime: Date.now() + (timeLimit * 1000),
                    points: points,
                    active: true,
                    solvedBy: []
                }
            });
            setShowLaunchQuizModal(false);
            setCoachQuizViewMode('MONITOR');
            toast.success("Position Launched as Live Quiz for all students! 🚀", { id: loadingId });
        } catch (e) {
            console.error("Error launching quiz:", e);
            toast.error("Failed to launch quiz", { id: loadingId });
        }
    };

    const switchMode = async (newMode) => {
        await updateDoc(doc(db, "classes", activeClass?.id), { classMode: newMode, miniGameType: null, arcadeData: null });
        if (newMode !== 'PRACTICE') logActivity({ title: `Switched to ${newMode.replace('_', ' ')} Mode` });
    };

    const syncWhiteboard = async () => {
        if (!activeClass) return toast.error("No active class!");
        setNotesSyncStatus('saving');
        try {
            await updateDoc(doc(db, "classes", activeClass.id), { whiteboardText: whiteboardInput });
            setNotesSyncStatus('saved');
            toast.success("Notes Synced!");
        } catch (err) {
            setNotesSyncStatus('error');
            toast.error("Failed to sync notes.");
        }
    };
    const clearWhiteboard = async () => {
        if (!activeClass) return;
        setNotesSyncStatus('saving');
        try {
            setWhiteboardInput('');
            await updateDoc(doc(db, "classes", activeClass.id), { whiteboardText: '' });
            setNotesSyncStatus('saved');
            toast.success("Notes Cleared!");
        } catch (err) {
            setNotesSyncStatus('error');
            toast.error("Failed to clear notes.");
        }
    };

    const copyFenToClipboard = () => {
        const fen = activeClass?.currentFen || activeClass?.fen || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
        navigator.clipboard.writeText(fen);
        toast.success("Board position (FEN) copied to clipboard! 📋");
    };

    const copyPgnToClipboard = () => {
        const moveHistory = activeClass?.moveHistory || [];
        if (moveHistory.length === 0) {
            toast.error("No moves played yet!");
            return;
        }
        let pgnString = "";
        for (let i = 0; i < moveHistory.length; i += 2) {
            const moveNum = Math.floor(i / 2) + 1;
            const whiteMove = moveHistory[i];
            const blackMove = moveHistory[i + 1] || "";
            pgnString += `${moveNum}. ${whiteMove} ${blackMove} `.trim() + " ";
        }
        navigator.clipboard.writeText(pgnString.trim());
        toast.success("Move history (PGN) copied to clipboard! 📋");
    };
    const handleAddLiveStudent = async (email) => {
        if (!email) return;
        const loadingId = toast.loading("Checking student availability...");
        try {
            // Query classes to see if student is already attending another active LIVE class
            const q = query(
                collection(db, "classes"),
                where("status", "==", "LIVE"),
                where("students", "array-contains", email)
            );
            const snap = await getDocs(q);
            
            let alreadyInClass = null;
            snap.forEach(docSnap => {
                if (docSnap.id !== activeClass.id) {
                    alreadyInClass = docSnap.data();
                }
            });
            
            if (alreadyInClass) {
                toast.dismiss(loadingId);
                const coachName = alreadyInClass.teacherName || alreadyInClass.teacherId || "another coach";
                const topic = alreadyInClass.topic || "Chess Lesson";
                
                const proceed = window.confirm(
                    `⚠️ WARNING: ${usersMap[email] || email} is already attending a live class!\n\n` +
                    `• Class Topic: "${topic}"\n` +
                    `• Coach: ${coachName}\n\n` +
                    `Adding them will cause overlaps. Do you still want to add them to your class?`
                );
                if (!proceed) return;
            } else {
                toast.dismiss(loadingId);
            }
            
            const updatedStudents = [...new Set([...(activeClass.students || []), email])]; 
            await updateDoc(doc(db, "classes", activeClass.id), { students: updatedStudents }); 
            setLiveSearch(''); 
            toast.success(`${usersMap[email] || 'Student'} added!`);
        } catch (e) {
            console.error(e);
            toast.dismiss(loadingId);
            toast.error("Error adding student.");
        }
    };
    const handleBoardControlChange = async (studentEmail, value) => { const newControl = { ...(activeClass.boardControl || {}) }; if (value === 'none') delete newControl[studentEmail]; else newControl[studentEmail] = value; await updateDoc(doc(db, "classes", activeClass.id), { boardControl: newControl }); };

    const handleAssignIndividualBoard = async (studentEmail) => {
        const safeEmail = studentEmail.replace(/\./g, '_');
        const startFen = activeClass.currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        await updateDoc(doc(db, "classes", activeClass.id), {
            [`individualBoards.${safeEmail}`]: {
                active: true,
                fen: startFen
            },
            [`studentFens.${safeEmail}`]: startFen
        });
        setShowSoloGrid(true);
        toast.success(`Custom board assigned to ${usersMap[studentEmail] || studentEmail.split('@')[0]}!`);
    };

    const handleRevertIndividualBoard = async (studentEmail) => {
        const safeEmail = studentEmail.replace(/\./g, '_');
        await updateDoc(doc(db, "classes", activeClass.id), {
            [`individualBoards.${safeEmail}.active`]: false
        });
        toast.success(`Returned ${usersMap[studentEmail] || studentEmail.split('@')[0]} to Master board.`);
    };

    const handleStartAllSoloPlay = async () => {
        if (!activeClass) return;
        const updates = {};
        const startFen = activeClass.currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        activeClass.students?.forEach(email => {
            const safeEmail = email.replace(/\./g, '_');
            updates[`individualBoards.${safeEmail}`] = {
                active: true,
                fen: startFen
            };
            updates[`studentFens.${safeEmail}`] = startFen;
        });
        await updateDoc(doc(db, "classes", activeClass.id), updates);
        setShowSoloGrid(true);
        toast.success("Solo boards activated for all students! ⚔️");
    };

    const handleStopAllSoloPlay = async () => {
        if (!activeClass) return;
        const updates = {};
        activeClass.students?.forEach(email => {
            const safeEmail = email.replace(/\./g, '_');
            updates[`individualBoards.${safeEmail}.active`] = false;
        });
        await updateDoc(doc(db, "classes", activeClass.id), updates);
        toast.success("Solo boards stopped for all students.");
    };

    const handleAssignQuickHW = async (studentEmail) => {
        if (!activeClass?.currentFen) return toast.error("No board to assign!");
        const newPuzzle = { fen: activeClass.currentFen, desc: "Quick Coach Assignment", solution: "Solution" };
        const loading = toast.loading("Assigning Quick HW...");
        try {
            await addDoc(collection(db, "homeworks"), { title: `Live Quick HW - ${new Date().toLocaleDateString()}`, dueDate: "", assignedBy: user.email.toLowerCase(), assignedTo: [studentEmail], materials: [newPuzzle], completedBy: [], createdAt: new Date().toISOString() });
            toast.success(`Quick HW assigned to ${usersMap[studentEmail]}!`, { id: loading });
            setSelectedStudentAction(null);
        } catch (e) { toast.error("Failed to assign HW", { id: loading }); }
    };

    const handlePairPresetChange = (e) => { const val = e.target.value; setPairPreset(val); if (val !== 'custom') { const [b, i] = val.split('+'); setPairBaseMins(parseInt(b)); setPairIncSecs(parseInt(i)); } };
    const handleAddPair = () => {
        if (!pairWhite || !pairBlack) return toast.error("Select both players!");
        if (pairWhite === pairBlack) return toast.error("A player can't play themselves!");
        const tBase = parseInt(pairBaseMins); const tInc = parseInt(pairIncSecs);
        const newPair = { id: `game_${Date.now()}`, white: pairWhite, black: pairBlack, timeControl: tBase * 60, increment: tInc, fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', pgn: '', status: 'PENDING' };
        setPendingPairs([...pendingPairs, newPair]); setPairWhite(''); setPairBlack('');
    };
    const startPairings = async () => {
        if (pendingPairs.length === 0) return toast.error("No pairs added!");
        const activeGames = {};
        pendingPairs.forEach(p => { activeGames[p.id] = { white: p.white, black: p.black, fen: p.fen, pgn: '', timeControl: p.timeControl, increment: p.increment, whiteTime: p.timeControl, blackTime: p.timeControl, turn: 'w', lastMoveAt: Date.now(), status: 'ACTIVE' }; });
        await updateDoc(doc(db, "classes", activeClass.id), { classMode: 'PAIRING', activeGames });
        logActivity({ title: 'Started Live Pair Arena Matches' });
        setPendingPairs([]); setRightTab('participants'); toast.success("Arena Pairings Started! ⚔️");
    };
    const cancelActiveGame = (gameId) => {
        setGameToCancel(gameId);
    };

    const confirmCancelActiveGame = async () => {
        if (!gameToCancel || !activeClass) return;
        const gId = gameToCancel;
        setGameToCancel(null);
        const updatedGames = { ...(activeClass.activeGames || {}) };
        delete updatedGames[gId];
        await updateDoc(doc(db, "classes", activeClass.id), { activeGames: updatedGames });
        toast.success("Match stopped and cancelled.");
    };

    const inputStyle = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box', fontSize: '13px' };
    const modeBtn = { padding: '5px 8px', borderRadius: '6px', border: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' };

    if (!activeClass || !isEmbedded) {
        if (isEmbedded) {
            return (
                <div style={{width: '100%' }}>
                    <EndClassModal show={showEndClassModal} onClose={() => setShowEndClassModal(false)} onConfirm={confirmEndClass} classesList={classesList} classId={classToEnd} usersMap={usersMap} />
                    <CoachHome user={user} myProfile={myProfile} myBatches={myBatches} allStudents={allStudents} allAcademyStudents={allAcademyStudents} classesList={classesList} changeStatus={changeStatus} />
                </div>
            );
        }
        return (
            <div style={{width: '100%', paddingBottom: '40px', maxWidth: '1000px', margin: '0 auto' }}>
                <EndClassModal show={showEndClassModal} onClose={() => setShowEndClassModal(false)} onConfirm={confirmEndClass} classesList={classesList} classId={classToEnd} usersMap={usersMap} />
                <div className="clay-card" style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)', 
                    padding: '20px 30px', 
                    borderRadius: '28px', 
                    marginBottom: '30px', 
                    border: '3px solid #ffffff', 
                    boxShadow: '0 15px 35px rgba(109, 40, 217, 0.3)', 
                    flexWrap: 'wrap', 
                    gap: '15px' 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        <CartoonAvatar
                            name={myProfile.name || 'Coach'}
                            size={60}
                            avatarUrl={myProfile?.avatar}
                            showBadge={true}
                        />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h1 style={{ color: '#ffffff', margin: 0, fontSize: '24px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                    Coach {myProfile.name || 'Instructor'}
                                </h1>
                                <span style={{ 
                                    background: 'rgba(255, 255, 255, 0.25)', 
                                    color: '#ffffff', 
                                    fontSize: '11px', 
                                    fontWeight: '900', 
                                    padding: '4px 10px', 
                                    borderRadius: '30px',
                                    border: '1px solid rgba(255, 255, 255, 0.4)'
                                }}>
                                    Academy Instructor 🎓
                                </span>
                            </div>
                            <p style={{ color: 'rgba(255, 255, 255, 0.85)', margin: '5px 0 0 0', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <img src={iconPawn3d} alt="Pawn" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />
                                Teaching at Hayaa Chess Academy
                            </p>
                        </div>
                    </div>

                    {/* Center: Teaching Progress Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 200px', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '280px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '900', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '4px' }}>
                                <span>⚡ Experience Points</span>
                                <span>{myProfile.totalXp || 1250} XP</span>
                            </div>
                            <div style={{ width: '100%', height: '12px', background: 'rgba(255, 255, 255, 0.3)', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(255, 255, 255, 0.5)' }}>
                                <div style={{ width: '75%', height: '100%', background: 'linear-gradient(90deg, #38bdf8, #818cf8)', borderRadius: '10px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Right: Quick Stats & Customize */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ 
                            background: 'rgba(255, 255, 255, 0.2)', 
                            padding: '8px 16px', 
                            borderRadius: '16px', 
                            boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.1), 2px 2px 5px rgba(0,0,0,0.1)', 
                            border: '1.5px solid rgba(255, 255, 255, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '16px' }}>👥</span>
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: '900', color: '#ffffff', lineHeight: 1.1 }}>{allStudents.length}</div>
                                <div style={{ fontSize: '9px', fontWeight: '900', color: 'rgba(255, 255, 255, 0.85)' }}>STUDENTS</div>
                            </div>
                        </div>
                        
                        <button 
                            onClick={() => setDashTab('settings')}
                            className="kid-btn-bubble"
                            style={{ 
                                padding: '10px', 
                                background: 'rgba(255, 255, 255, 0.25)', 
                                color: '#ffffff', 
                                border: '2px solid rgba(255, 255, 255, 0.6)', 
                                borderRadius: '50%', 
                                width: '38px',
                                height: '38px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                                fontSize: '16px'
                            }}
                            title="Coach Settings"
                        >
                            ⚙️
                        </button>
                    </div>
                </div>
                <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    marginBottom: '30px', 
                    flexWrap: 'wrap', 
                    background: 'var(--bg-primary)', 
                    padding: '8px 12px', 
                    borderRadius: '16px', 
                    boxShadow: 'var(--neu-shadow-inset)',
                    width: 'fit-content'
                }}>
                    <button 
                        onClick={() => setDashTab('home')} 
                        style={{ 
                            padding: '10px 20px', 
                            background: dashTab === 'home' ? 'var(--bg-primary)' : 'transparent', 
                            color: dashTab === 'home' ? '#3b82f6' : 'var(--text-secondary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            boxShadow: dashTab === 'home' ? 'var(--neu-shadow-inset)' : 'none',
                            border: dashTab === 'home' ? '1.5px solid rgba(255, 255, 255, 0.65)' : '1.5px solid transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        Home HQ
                    </button>
                    <button 
                        onClick={() => setDashTab('history')} 
                        style={{ 
                            padding: '10px 20px', 
                            background: dashTab === 'history' ? 'var(--bg-primary)' : 'transparent', 
                            color: dashTab === 'history' ? '#ec4899' : 'var(--text-secondary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            boxShadow: dashTab === 'history' ? 'var(--neu-shadow-inset)' : 'none',
                            border: dashTab === 'history' ? '1.5px solid rgba(255, 255, 255, 0.65)' : '1.5px solid transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                        History
                    </button>
                    <button 
                        onClick={() => setDashTab('curriculum')} 
                        style={{ 
                            padding: '10px 20px', 
                            background: dashTab === 'curriculum' ? 'var(--bg-primary)' : 'transparent', 
                            color: dashTab === 'curriculum' ? '#f59e0b' : 'var(--text-secondary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            boxShadow: dashTab === 'curriculum' ? 'var(--neu-shadow-inset)' : 'none',
                            border: dashTab === 'curriculum' ? '1.5px solid rgba(255, 255, 255, 0.65)' : '1.5px solid transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                        Curriculum Library
                    </button>
                    <button 
                        onClick={() => setDashTab('performance')} 
                        style={{ 
                            padding: '10px 20px', 
                            background: dashTab === 'performance' ? 'var(--bg-primary)' : 'transparent', 
                            color: dashTab === 'performance' ? '#a855f7' : 'var(--text-secondary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            boxShadow: dashTab === 'performance' ? 'var(--neu-shadow-inset)' : 'none',
                            border: dashTab === 'performance' ? '1.5px solid rgba(255, 255, 255, 0.65)' : '1.5px solid transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                        Student Tracker
                    </button>
                    <button 
                        onClick={() => setDashTab('settings')} 
                        style={{ 
                            padding: '10px 20px', 
                            background: dashTab === 'settings' ? 'var(--bg-primary)' : 'transparent', 
                            color: dashTab === 'settings' ? '#10b981' : 'var(--text-secondary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            boxShadow: dashTab === 'settings' ? 'var(--neu-shadow-inset)' : 'none',
                            border: dashTab === 'settings' ? '1.5px solid rgba(255, 255, 255, 0.65)' : '1.5px solid transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        Settings
                    </button>
                </div>
                <div key={dashTab} className="animate-fade-in" style={{ width: '100%' }}>
                    {dashTab === 'home' && ( <CoachDashboardHome user={user} myProfile={myProfile} myBatches={myBatches} allStudents={allStudents} allAcademyStudents={allAcademyStudents} classesList={classesList} changeStatus={changeStatus} activeClass={activeClass} setActiveTab={setActiveTab} setDashTab={setDashTab} /> )}
                    {dashTab === 'history' && ( <CoachHistory classesList={classesList} usersMap={usersMap} /> )}
                    {dashTab === 'curriculum' && ( <CoachCurriculum activeClass={activeClass} /> )}
                    {dashTab === 'performance' && ( <CoachPerformance allAcademyStudents={allAcademyStudents} /> )}
                    {dashTab === 'settings' && ( <ProfileSettings myProfile={myProfile} /> )}
                </div>
            </div>
        );
    }

    const currentMode = activeClass.classMode || 'PRACTICE';
    const liveStudentsList = activeClass.students || [];

    const renderModeSlider = () => {
        let slideLeft = '0%';
        let slideColor = '#10b981';
        if (currentMode === 'MINIGAME') { slideLeft = '25%'; slideColor = '#f59e0b'; }
        else if (rightTab === 'arcade') { slideLeft = '50%'; slideColor = '#8b5cf6'; }
        else if (rightTab === 'pairings' || currentMode === 'PAIRING') { slideLeft = '75%'; slideColor = '#ec4899'; }

        return (
            <div style={{position: 'relative', display: 'flex', background: 'var(--bg-primary)', padding: '4px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)', overflow: 'hidden', flexShrink: 0 }}>
                <div style={{position: 'absolute', top: 4, bottom: 4, left: slideLeft, width: 'calc(25% - 3px)', background: slideColor, borderRadius: '5px', transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)', zIndex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}></div>
                <button onClick={() => switchMode('PRACTICE')} style={{flex: 1, ...modeBtn, background: 'transparent', color: (currentMode === 'PRACTICE' && rightTab !== 'arcade' && rightTab !== 'pairings') ? 'white' : 'var(--text-secondary)', zIndex: 2, position: 'relative' }}>👨‍🏫 Teach</button>
                <button onClick={() => switchMode('MINIGAME')} style={{flex: 1, ...modeBtn, background: 'transparent', color: currentMode === 'MINIGAME' ? 'white' : 'var(--text-secondary)', zIndex: 2, position: 'relative' }}>🍎 Basics</button>
                <button onClick={() => { switchMode('PRACTICE'); setRightTab('arcade'); }} style={{flex: 1, ...modeBtn, background: 'transparent', color: rightTab === 'arcade' ? 'white' : 'var(--text-secondary)', zIndex: 2, position: 'relative' }}>🎮 Arcade</button>
                <button onClick={() => { 
                    const hasGames = Object.keys(activeClass.activeGames || {}).length > 0;
                    switchMode(hasGames ? 'PAIRING' : 'PRACTICE'); 
                    setRightTab('pairings'); 
                }} style={{flex: 1, ...modeBtn, background: 'transparent', color: (rightTab === 'pairings' || currentMode === 'PAIRING') ? 'white' : 'var(--text-secondary)', zIndex: 2, position: 'relative' }}>👥 Arena</button>
            </div>
        );
    };

    const CLASSROOM_TACTICAL_TOPICS = [
        { id: 1, name: "Mate in 1", icon: "🎯", tag: "mateIn1", desc: "Immediate checkmate" },
        { id: 2, name: "Mate in 2", icon: "♟️", tag: "mateIn2", desc: "2-move checkmates" },
        { id: 3, name: "Mate in 3+", icon: "🔥", tag: "mateIn3", desc: "Complex combinations" },
        { id: 4, name: "Back-Rank Mate", icon: "🚪", tag: "backRankMate", desc: "Back-rank weakness" },
        { id: 5, name: "Smothered Mate", icon: "🗜️", tag: "smotheredMate", desc: "King smothered by own pieces" },
        { id: 6, name: "Anastasia's Mate", icon: "🏰", tag: "anastasiaMate", desc: "Rook & Knight combo" },
        { id: 7, name: "Arabian Mate", icon: "🐪", tag: "arabianMate", desc: "Corner trap" },
        { id: 8, name: "The Fork", icon: "🔱", tag: "fork", desc: "Attack two pieces at once" },
        { id: 9, name: "The Pin", icon: "📌", tag: "pin", desc: "Paralyze pieces" },
        { id: 10, name: "The Skewer", icon: "🍢", tag: "skewer", desc: "X-Ray attack" },
        { id: 11, name: "Discovered Attack", icon: "👻", tag: "discoveredAttack", desc: "Hidden attack" },
        { id: 12, name: "Double Check", icon: "‼️", tag: "doubleCheck", desc: "Most powerful check" },
        { id: 13, name: "Remove Defender", icon: "💥", tag: "capturingDefender", desc: "Destroy the guard" },
        { id: 14, name: "Deflection", icon: "🧲", tag: "deflection", desc: "Force defender away" },
        { id: 15, name: "Attraction", icon: "🎣", tag: "attraction", desc: "Lure to fatal square" },
        { id: 16, name: "Clearance", icon: "🧹", tag: "clearance", desc: "Clear a line" },
        { id: 17, name: "Intermezzo", icon: "⚡", tag: "intermezzo", desc: "In-between move" },
        { id: 18, name: "Interference", icon: "🚧", tag: "interference", desc: "Block defender" },
        { id: 19, name: "X-Ray Attack", icon: "🔫", tag: "xRayAttack", desc: "Through a piece" },
        { id: 20, name: "Zugzwang", icon: "🛑", tag: "zugzwang", desc: "Bad forced move" },
        { id: 21, name: "Hanging Piece", icon: "🍎", tag: "hangingPiece", desc: "Take free piece" },
        { id: 22, name: "Defensive Move", icon: "🛡️", tag: "defensiveMove", desc: "Escape/defend" },
        { id: 23, name: "Trapped Piece", icon: "🕸️", tag: "trappedPiece", desc: "Cut off escape" },
        { id: 24, name: "Winning Advantage", icon: "📈", tag: "crushing", desc: "Convert advantage" },
        { id: 25, name: "Sacrifice", icon: "🔥", tag: "sacrifice", desc: "Give material" },
        { id: 26, name: "Exposed King", icon: "🎯", tag: "exposedKing", desc: "No pawn shield" },
        { id: 27, name: "Endgame Master", icon: "⏳", tag: "endgame", desc: "Final phase" },
        { id: 28, name: "Pawn Endgame", icon: "♟️", tag: "pawnEndgame", desc: "K+P endings" },
        { id: 29, name: "Rook Endgame", icon: "♖", tag: "rookEndgame", desc: "Rook endings" },
        { id: 30, name: "Opening Traps", icon: "🪤", tag: "opening", desc: "Opening tricks" },
        { id: 31, name: "Middlegame", icon: "⚔️", tag: "middlegame", desc: "Tactical battles" },
        { id: 32, name: "Advanced Pawn", icon: "🚀", tag: "advancedPawn", desc: "Passed pawns" },
        { id: 33, name: "Promotion", icon: "👑", tag: "promotion", desc: "Pawn promotion" },
        { id: 34, name: "Underpromotion", icon: "🐴", tag: "underPromotion", desc: "Promote to minor" },
        { id: 35, name: "En Passant", icon: "👻", tag: "enPassant", desc: "Special pawn rule" }
    ];

    const renderTabContent = () => {
        if (rightTab === 'curriculum') return <CurriculumViewer activeClass={activeClass} syncToLiveBoard={handleSyncToLiveBoard} switchMode={switchMode} />;
        if (rightTab === 'tactics') {
            // Helper to fetch a random puzzle from Lichess by theme+rating and load it on the main board with question text
            const fetchAndLoadPuzzle = async (tag, lvl) => {
                if (!activeClass) return toast.error('No active class!');
                const loadingId = toast.loading(`Fetching ${tacticTopic?.name} puzzle...`);
                try {
                    const res = await fetch(`https://lichess.org/api/puzzle/next?theme=${tag}&difficulty=normal`, {
                        headers: { 'Accept': 'application/json' }
                    });
                    if (!res.ok) throw new Error('Lichess API error');
                    const data = await res.json();
                    const initialFen = data?.puzzle?.initialFen;
                    const solution = data?.puzzle?.solution || [];
                    if (!initialFen) throw new Error('No FEN returned');
                    const t = new Chess();
                    t.load(initialFen);
                    const puzzleFen = t.fen();
                    toast.dismiss(loadingId);
                    
                    await handleSyncToLiveBoard(puzzleFen, 'fen');
                    await updateDoc(doc(db, "classes", activeClass.id), {
                        topicNote: `${tacticTopic.icon} ${tacticTopic.name} — Level ${lvl} (~${500 + lvl * 100})`
                    }).catch(e=>console.error(e));

                    if (solution.length > 0) toast.success(`Puzzle loaded! Question: ${tacticTopic.name} (Solution: ${solution.join(', ')}) ♟️`, { duration: 6000 });
                    else toast.success(`Puzzle loaded on board! ♟️`);
                } catch(e) {
                    console.error(e);
                    const fallbackFens = [
                        'r1bqkb1r/pppp1ppp/2n2n2/4p1B1/2B1P3/8/PPPP1PPP/RN1QK1NR b KQkq - 3 4',
                        'r1bq1rk1/ppp2ppp/2np1n2/2b1p3/2B1P3/2NP1N2/PPP2PPP/R1BQK2R w KQ - 0 7',
                        '2r1r1k1/pp3ppp/2n5/3q4/3P4/2N2N2/PP3PPP/R2QR1K1 b - - 0 1'
                    ];
                    const fallbackFen = fallbackFens[Math.min(lvl - 1, 2)];
                    toast.dismiss(loadingId);
                    await handleSyncToLiveBoard(fallbackFen, 'fen');
                    await updateDoc(doc(db, "classes", activeClass.id), {
                        topicNote: `${tacticTopic.icon} ${tacticTopic.name} — Level ${lvl}`
                    }).catch(err=>console.error(err));
                    toast.success('Sample position loaded on board!', { duration: 4000 });
                }
            };

            const launchClassQuiz = async (tag, lvl) => {
                if (!activeClass) return toast.error('No active class!');
                const loadingId = toast.loading(`Launching ${tacticTopic.name} Level ${lvl} Quiz...`);
                try {
                    let puzzleFen = '';
                    let solutionMoves = [];
                    try {
                        const res = await fetch(`https://lichess.org/api/puzzle/next?theme=${tag}&difficulty=normal`, {
                            headers: { 'Accept': 'application/json' }
                        });
                        if (res.ok) {
                            const data = await res.json();
                            puzzleFen = data?.puzzle?.initialFen;
                            solutionMoves = data?.puzzle?.solution || [];
                        }
                    } catch(e){}

                    if (!puzzleFen) {
                        const fallbackFens = [
                            'r1bqkb1r/pppp1ppp/2n2n2/4p1B1/2B1P3/8/PPPP1PPP/RN1QK1NR b KQkq - 3 4',
                            'r1bq1rk1/ppp2ppp/2np1n2/2b1p3/2B1P3/2NP1N2/PPP2PPP/R1BQK2R w KQ - 0 7',
                            '2r1r1k1/pp3ppp/2n5/3q4/3P4/2N2N2/PP3PPP/R2QR1K1 b - - 0 1'
                        ];
                        puzzleFen = fallbackFens[Math.min(lvl - 1, 2)];
                        solutionMoves = ['e4'];
                    }

                    await updateDoc(doc(db, "classes", activeClass.id), {
                        classMode: 'QUIZ',
                        currentFen: puzzleFen,
                        studentFens: {},
                        quizAttempts: {},
                        quizData: {
                            question: `${tacticTopic.icon} ${tacticTopic.name} — Level ${lvl}`,
                            answers: solutionMoves,
                            endTime: Date.now() + 120000,
                            points: 10,
                            active: true,
                            solvedBy: []
                        }
                    });
                    toast.success(`Position Launched as Live Quiz to all students! 🚀`, { id: loadingId });
                } catch(e) {
                    console.error(e);
                    toast.error('Failed to launch live quiz!', { id: loadingId });
                }
            };

            return (
                <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
                    {tacticTopic ? (
                        <>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button onClick={() => setTacticTopic(null)} style={{ padding: '4px 10px', background: 'var(--bg-primary)', border: '1px solid rgba(163,177,198,0.4)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 'bold', fontSize: '11px', boxShadow: 'var(--neu-shadow)' }}>⬅ Back</button>
                                <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>{tacticTopic.icon} {tacticTopic.name}</span>
                            </div>

                            {/* Two Action Cards */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', background: 'rgba(59,130,246,0.06)', borderRadius: '10px', border: '1px solid rgba(59,130,246,0.2)' }}>
                                <div style={{ fontSize: '10px', color: '#3b82f6', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🎯 Load on Main Board</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Fetch a puzzle position and show it on the coach board — all students see it live.</div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(lvl => {
                                    const rating = 500 + lvl * 100;
                                    return (
                                        <div key={lvl} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-primary)', padding: '7px 10px', borderRadius: '8px', border: '1px solid rgba(163,177,198,0.3)', boxShadow: 'var(--neu-shadow)', gap: '6px' }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-primary)' }}>Level {lvl} <span style={{ color: '#64748b', fontWeight: 'normal', fontSize: '10px' }}>~{rating}</span></div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                                                <button
                                                    onClick={() => fetchAndLoadPuzzle(tacticTopic.tag, lvl)}
                                                    title="Fetch puzzle and load position on main board with topic question"
                                                    style={{ padding: '5px 9px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(37,99,235,0.35)' }}
                                                >
                                                    ♟️ Board
                                                </button>
                                                <button
                                                    onClick={() => launchClassQuiz(tacticTopic.tag, lvl)}
                                                    title="Launch this tactical puzzle as a Live Quiz for all students"
                                                    style={{ padding: '5px 9px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(16,185,129,0.35)' }}
                                                >
                                                    📋 Class
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🧩 Tactical Syllabus — 35 Topics</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Select a topic → then choose ♟️ Board (load position) or 📋 Class (assign to students)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                {CLASSROOM_TACTICAL_TOPICS.map(topic => (
                                    <div
                                        key={topic.id}
                                        onClick={() => setTacticTopic(topic)}
                                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '8px 4px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid rgba(163,177,198,0.3)', cursor: 'pointer', boxShadow: 'var(--neu-shadow)', transition: 'all 0.15s', textAlign: 'center' }}
                                        onMouseOver={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                        onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(163,177,198,0.3)'; e.currentTarget.style.transform = 'none'; }}
                                    >
                                        <span style={{ fontSize: '20px' }}>{topic.icon}</span>
                                        <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-primary)', lineHeight: 1.2 }}>{topic.name}</span>
                                        <span style={{ fontSize: '9px', color: 'var(--text-secondary)', lineHeight: 1.2 }}>{topic.desc}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            );
        }

        if (rightTab === 'moves') {
            const gameTree = activeClass?.gameTree || convertLegacyToTree(
                activeClass?.moveHistory || [],
                activeClass?.variations || [],
                activeClass?.moveComments || {}
            );

            const handleUpdateTreeFromCoach = async (updatedTree) => {
                if (!activeClass?.id) return;
                const currNode = updatedTree.nodes[updatedTree.currentNodeId];
                const newFen = currNode ? currNode.fen : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

                await updateDoc(doc(db, "classes", activeClass.id), {
                    gameTree: updatedTree,
                    currentFen: newFen
                });
            };

            const handleSelectNodeFromCoach = async (node) => {
                if (!activeClass?.id || !node) return;
                await updateDoc(doc(db, "classes", activeClass.id), {
                    currentFen: node.fen
                });
            };

            return (
                <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: '380px' }}>
                    <LichessNotationTree
                        gameTree={gameTree}
                        onUpdateTree={handleUpdateTreeFromCoach}
                        onSelectNode={handleSelectNodeFromCoach}
                        role="COACH"
                    />
                </div>
            );
        }
        if (rightTab === 'arcade') return <ArcadePanel activeClass={activeClass} />;
        if (rightTab === 'leaderboard') {
            const studentScores = activeClass.studentScores || {};
            const solvedBy = activeClass.quizData?.solvedBy || [];
            const sortedStudents = [...liveStudentsList].sort((a, b) => {
                const scoreA = studentScores[a.replace(/\./g, '_')] || 0;
                const scoreB = studentScores[b.replace(/\./g, '_')] || 0;
                if (scoreB !== scoreA) return scoreB - scoreA;
                return (usersMap[a] || a).localeCompare(usersMap[b] || b);
            });

            return (
                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, gap: '10px', overflowY: 'auto', boxSizing: 'border-box', height: '100%' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>🏆 Class Leaderboard</span>
                        <span style={{ fontSize: '11px', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Live Scores</span>
                    </div>

                    {sortedStudents.length === 0 ? (
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'center', marginTop: '30px', fontStyle: 'italic' }}>
                            No students in class yet.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {sortedStudents.map((email, idx) => {
                                const safeEmail = email.replace(/\./g, '_');
                                const points = studentScores[safeEmail] || 0;
                                const isSolved = solvedBy.includes(email);
                                const isOnline = activeClass.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
                                const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`;
                                
                                return (
                                    <div 
                                        key={email} 
                                        style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            padding: '10px 12px', 
                                            background: idx === 0 ? 'rgba(245, 158, 11, 0.1)' : 'var(--bg-primary)', 
                                            borderRadius: '10px', 
                                            border: idx === 0 ? '1.5px solid #f59e0b' : '1px solid rgba(163, 177, 198, 0.3)',
                                            boxShadow: 'var(--neu-shadow)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '16px', fontWeight: '900', minWidth: '24px', textAlign: 'center' }}>{medal}</span>
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {usersMap[email] || email.split('@')[0]}
                                                    {isOnline && <span style={{ width: '7px', height: '7px', background: '#10b981', borderRadius: '50%' }} title="Online"></span>}
                                                </div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                                                    {activeClass.quizData?.active ? (
                                                        isSolved ? `✅ Solved! (+${activeClass.quizData.points || 0} XP pending)` : '⏳ Solving puzzle...'
                                                    ) : (
                                                        '🏆 Total Class XP'
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '900', color: '#f59e0b' }}>
                                                {points} XP
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }
        if (rightTab === 'library') return <LibraryManager userProfile={myProfile || user} activeClassId={activeClass.id} syncToLiveBoard={handleSyncToLiveBoard} />;
        if (rightTab === 'diary') {
            const studentRemarks = activeClass.studentClassRemarks || {};
            return (
                <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', flex: 1, gap: '15px', overflowY: 'auto', boxSizing: 'border-box', height: '100%' }}>
                    {/* Class Private Notes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            📓 Class Private Diary (Coaching Notes)
                        </label>
                        <textarea
                            value={activeClass.coachClassPrivateNotes || ''}
                            onChange={async (e) => {
                                const val = e.target.value;
                                await updateDoc(doc(db, "classes", activeClass.id), {
                                    coachClassPrivateNotes: val
                                }).catch(err => console.error(err));
                            }}
                            placeholder="Jot down lesson details, homework to assign next time, general notes..."
                            rows="4"
                            style={{ 
                                padding: '10px 12px', 
                                background: '#e0e5ec', 
                                border: '1px solid rgba(255, 255, 255, 0.45)', 
                                borderRadius: '10px', 
                                color: 'var(--text-primary)', 
                                fontSize: '13px', 
                                outline: 'none', 
                                boxShadow: 'var(--neu-shadow-inset)',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />
                    </div>

                    <div style={{ height: '1px', background: 'rgba(163, 177, 198, 0.3)' }}></div>

                    {/* Student remarks */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            👤 Student Class Remarks
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {liveStudentsList.length === 0 ? (
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No students currently in class.</div>
                            ) : (
                                liveStudentsList.map(email => {
                                    const safeEmail = email.replace(/\./g, '_');
                                    const remark = studentRemarks[safeEmail] || '';
                                    return (
                                        <div key={email} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'var(--bg-primary)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.5)', boxShadow: '2px 2px 5px rgba(163, 177, 198, 0.25), -2px -2px 5px rgba(255, 255, 255, 0.7)' }}>
                                            <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 'bold' }}>
                                                👤 {usersMap[email] || email.split('@')[0]}
                                            </span>
                                            <input
                                                type="text"
                                                value={remark}
                                                onChange={async (e) => {
                                                    const val = e.target.value;
                                                    await updateDoc(doc(db, "classes", activeClass.id), {
                                                        [`studentClassRemarks.${safeEmail}`]: val
                                                    }).catch(err => console.error(err));
                                                }}
                                                placeholder="Remarks for this student in today's class..."
                                                style={{ 
                                                    padding: '8px 10px', 
                                                    background: '#e0e5ec', 
                                                    border: '1px solid rgba(255, 255, 255, 0.45)', 
                                                    borderRadius: '8px', 
                                                    color: 'var(--text-primary)', 
                                                    fontSize: '12px', 
                                                    outline: 'none', 
                                                    boxShadow: 'var(--neu-shadow-inset)',
                                                    width: '100%'
                                                }}
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        if (rightTab === 'pairings') return (
            <div style={{padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto', gap: '8px' }}>
                <div style={{background: 'rgba(236,72,153,0.08)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(236,72,153,0.3)' }}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        <div><label style={{color:'var(--text-secondary)', fontSize:'11px', fontWeight:'bold', display:'block', marginBottom:'3px' }}>⚪ White Player</label><select value={pairWhite} onChange={e=>setPairWhite(e.target.value)} style={inputStyle}><option value="">-- Select Student --</option><option value={user.email}>👨‍🏫 Coach (You)</option>{liveStudentsList.map(email => <option key={email} value={email}>{usersMap[email] || email}</option>)}</select></div>
                        <div><label style={{color:'var(--text-secondary)', fontSize:'11px', fontWeight:'bold', display:'block', marginBottom:'3px' }}>⚫ Black Player</label><select value={pairBlack} onChange={e=>setPairBlack(e.target.value)} style={inputStyle}><option value="">-- Select Opponent --</option><optgroup label="Coaches"><option value={user.email}>👨‍🏫 Coach (You)</option></optgroup><optgroup label="Live Students">{liveStudentsList.filter(e => e !== pairWhite).map(email => <option key={email} value={email}>{usersMap[email] || email}</option>)}</optgroup><optgroup label="Cute Bots">{BOTS.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</optgroup></select></div>
                        <div><label style={{color:'var(--text-secondary)', fontSize:'11px', fontWeight:'bold', display:'block', marginBottom:'3px' }}>⏳ Time Control</label><select value={pairPreset} onChange={handlePairPresetChange} style={inputStyle}><option value="5+0">5+0 (5 Min)</option><option value="10+0">10+0 (10 Min)</option></select></div>
                        <button onClick={handleAddPair} style={{padding: '8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>➕ Add to Queue</button>
                    </div>
                </div>
                {pendingPairs.length > 0 && <div style={{fontSize:'11px', color:'var(--text-secondary)' }}>{pendingPairs.length} pair(s) queued</div>}
                <button onClick={startPairings} disabled={pendingPairs.length === 0} style={{padding: '10px', background: '#ec4899', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: pendingPairs.length===0?'not-allowed':'pointer', opacity: pendingPairs.length===0?0.5:1 }}>🚀 Start All Matches</button>

                {/* Active Matches Section for Coach monitoring while teaching */}
                {Object.keys(activeClass.activeGames || {}).length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid rgba(163, 177, 198, 0.4)', paddingTop: '12px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#ec4899', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span>⚔️</span> Active Matches
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {Object.entries(activeClass.activeGames).map(([gId, g]) => {
                                const wLabel = resolvePlayerName(g.white);
                                const bLabel = resolvePlayerName(g.black);
                                return (
                                    <div key={gId} style={{ background: 'var(--bg-primary)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.3)', boxShadow: 'var(--neu-shadow)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>⚪ {wLabel} vs ⚫ {bLabel}</span>
                                            <span style={{ color: g.turn === 'w' ? '#2563eb' : '#10b981' }}>{g.turn === 'w' ? 'W' : 'B'}'s turn</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px', marginTop: '4px' }}>
                                            <button 
                                                onClick={() => setPlayingArenaGameId(gId)}
                                                style={{
                                                    flex: 1,
                                                    padding: '5px',
                                                    background: 'rgba(59,130,246,0.15)',
                                                    color: '#3b82f6',
                                                    border: '1px solid #3b82f6',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '9px'
                                                }}
                                            >
                                                👁️ Watch
                                            </button>
                                            <button 
                                                onClick={() => cancelActiveGame(gId)}
                                                style={{
                                                    padding: '5px 8px',
                                                    background: 'transparent',
                                                    border: '1px solid #ef4444',
                                                    color: '#ef4444',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '9px'
                                                }}
                                                title="Stop Match"
                                            >
                                                ❌ Stop
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
        if (rightTab === 'notes') {
            let statusText = "Saved & Synced";
            let statusColor = "#10b981";
            if (notesSyncStatus === 'typing') {
                statusText = "Typing (unsaved changes)...";
                statusColor = "#ca8a04";
            } else if (notesSyncStatus === 'saving') {
                statusText = "Auto-saving...";
                statusColor = "#3b82f6";
            } else if (notesSyncStatus === 'error') {
                statusText = "Auto-save failed.";
                statusColor = "#ef4444";
            }

            const toolbarBtnStyle = {
                padding: '4px 8px',
                background: 'rgba(255,255,255,0.04)',
                color: 'var(--text-primary)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
            };
            
            return (
                <div style={{padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, gap: '8px', height: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', background: 'rgba(0,0,0,0.1)', padding: '5px', borderRadius: '8px 8px 0 0', border: '1px solid rgba(163, 177, 198, 0.4)', borderBottom: 'none' }}>
                        <button onClick={() => executeStyle('bold')} style={toolbarBtnStyle} title="Bold"><b>B</b></button>
                        <button onClick={() => executeStyle('italic')} style={toolbarBtnStyle} title="Italic"><i>I</i></button>
                        <button onClick={() => executeStyle('underline')} style={toolbarBtnStyle} title="Underline"><u>U</u></button>
                        <button onClick={() => executeStyle('formatBlock', '<h3>')} style={toolbarBtnStyle} title="Heading 3">H3</button>
                        <button onClick={() => executeStyle('formatBlock', '<h4>')} style={toolbarBtnStyle} title="Heading 4">H4</button>
                        <button onClick={() => executeStyle('formatBlock', '<p>')} style={toolbarBtnStyle} title="Normal Paragraph">Normal</button>
                        <button onClick={() => executeStyle('insertUnorderedList')} style={toolbarBtnStyle} title="Bullet list">• List</button>
                        <button onClick={() => {
                            if (editorRef.current) {
                                editorRef.current.innerHTML = '';
                                setWhiteboardInput('');
                                setNotesSyncStatus('typing');
                            }
                        }} style={{ ...toolbarBtnStyle, marginLeft: 'auto', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }} title="Clear All">🧹</button>
                    </div>

                    <div 
                        ref={editorRef}
                        contentEditable
                        onInput={(e) => {
                            setWhiteboardInput(e.currentTarget.innerHTML);
                        }}
                        style={{
                            flex: 1, 
                            width: '100%', 
                            padding: '10px', 
                            borderRadius: '0 0 8px 8px', 
                            background: 'var(--bg-primary)', 
                            color: 'var(--text-primary)', 
                            border: '1px solid rgba(163, 177, 198, 0.4)', 
                            borderTop: 'none', 
                            boxSizing: 'border-box', 
                            fontSize: '13px', 
                            lineHeight: '1.5', 
                            minHeight: '120px',
                            overflowY: 'auto',
                            outline: 'none',
                            whiteSpace: 'pre-wrap',
                            textAlign: 'left'
                        }}
                        placeholder="Type your chess notes here..."
                    />
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-secondary)', padding: '0 2px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: statusColor, fontWeight: 'bold' }}>
                            {notesSyncStatus === 'typing' && '✍️'}
                            {notesSyncStatus === 'saving' && '⏳'}
                            {notesSyncStatus === 'saved' && '✅'}
                            {notesSyncStatus === 'error' && '❌'}
                            {statusText}
                        </span>
                    </div>

                    <div style={{display: 'flex', gap: '6px' }}>
                        <button onClick={syncWhiteboard} style={{flex: 1, padding: '10px', background: '#10b981', color: 'black', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>📤 Sync Notes</button>
                        <button onClick={() => setShowSaveLessonModal(true)} style={{flex: 1, padding: '10px', background: '#3b82f6', color: 'white', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>💾 Save Lesson</button>
                        <button onClick={clearWhiteboard} style={{padding: '10px', background: '#ef4444', color: 'white', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🗑️</button>
                    </div>
                </div>
            );
        }
        if (rightTab === 'participants') return (
            <div style={{padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, gap: '8px', overflowY: 'auto' }}>
                <input type="text" value={liveSearch} onChange={e=>setLiveSearch(e.target.value.toLowerCase())} placeholder="🔍 Search student to add..." style={{...inputStyle, padding: '7px 10px' }} />
                {liveSearch && (
                    <div style={{background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '6px', maxHeight: '100px', overflowY: 'auto' }}>
                        {allAcademyStudents.filter(s => !activeClass.students?.includes(s.email) && ((s.name || '').toLowerCase().includes(liveSearch))).map(s => (
                            <div key={s.email} onClick={() => handleAddLiveStudent(s.email)} style={{padding: '7px 10px', borderBottom: '1px solid rgba(163, 177, 198, 0.2)', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                <span>👤 {s.name || s.username || s.email.split('@')[0]} (@{s.username || s.email.split('@')[0]})</span><span style={{color: '#10b981', fontWeight: 'bold' }}>➕</span>
                            </div>
                        ))}
                    </div>
                )}
                <div style={{flex: 1, background: 'var(--bg-primary)', borderRadius: '8px', overflowY: 'auto', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                    {activeClass.students?.map((email, i) => {
                        const safeEmail = email.replace(/\./g, '_');
                        const isOnline = (Date.now() - (activeClass.presence?.[safeEmail] || 0)) < 45000;
                        const status = activeClass.studentStatus?.[safeEmail];
                        return (
                            <div key={i} style={{padding: '7px 10px', borderBottom: '1px solid rgba(163, 177, 198, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div onClick={() => setSelectedStudentAction(email)} style={{display: 'flex', alignItems: 'center', gap: '7px', flex: 1, cursor: 'pointer' }}>
                                    <span style={{position: 'relative', fontSize: '15px' }}>👤<span style={{position: 'absolute', bottom: -1, right: -2, width: '7px', height: '7px', background: isOnline ? '#10b981' : '#64748b', borderRadius: '50%', border: '1.5px solid var(--bg-primary)' }}></span></span>
                                    <span style={{fontSize: '12px', color: isOnline ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {usersMap[email] || email.split('@')[0]}
                                        {status === 'RAISED_HAND' && <span style={{ animation: 'wiggle 0.6s infinite', fontSize: '13px' }} title="Raised Hand!">🙋</span>}
                                        {status === 'DONE' && <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '12px' }} title="Finished task!">✅</span>}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <select value={activeClass.boardControl?.[email] || 'none'} onChange={(e) => handleBoardControlChange(email, e.target.value)} style={{padding: '3px 4px', borderRadius: '4px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', fontSize: '10px', cursor: 'pointer' }}>
                                        <option value="none">🔒 Lock</option>
                                        <option value="w">⚪ W</option>
                                        <option value="b">⚫ B</option>
                                        <option value="both">Both</option>
                                    </select>
                                    
                                    {activeClass.individualBoards?.[safeEmail]?.active ? (
                                        <div style={{ display: 'flex', gap: '3px' }}>
                                            <button 
                                                onClick={() => {
                                                    setSpectatingStudentEmail(email);
                                                    setShowSoloGrid(true);
                                                }} 
                                                title="Control Board"
                                                style={{ padding: '3px 6px', background: '#10b981', color: 'black', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                            >
                                                🎮 Play
                                            </button>
                                            <button 
                                                onClick={() => handleRevertIndividualBoard(email)} 
                                                title="Return to Master"
                                                style={{ padding: '3px 6px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                            >
                                                ❌
                                            </button>
                                        </div>
                                    ) : (
                                        <button 
                                            onClick={() => handleAssignIndividualBoard(email)} 
                                            title="Assign Solo Board"
                                            style={{ padding: '3px 6px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                        >
                                            ♟️ Solo
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
        return null;
    };

    return (
        <div className="class-dashboard-container">
            <style>{`
                .class-dashboard-container {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    padding: 8px 10px;
                    box-sizing: border-box;
                    overflow-y: auto;
                }
                @media (max-width: 1100px) {
                    .class-dashboard-container {
                        height: auto;
                        overflow: visible;
                    }
                }
                .coach-classroom { display: flex; flex-direction: row; gap: 10px; flex: 1; min-height: 0; overflow: visible; box-sizing: border-box; }
                .coach-board-area { flex: 1 1 auto; min-width: 0; height: 100%; overflow-y: auto; overflow-x: hidden; display: flex; flex-direction: column; gap: 6px; scrollbar-width: thin; scrollbar-color: #3b82f6 rgba(163, 177, 198, 0.25); }
                .coach-board-area::-webkit-scrollbar {
                    width: 12px;
                    display: block !important;
                }
                .coach-board-area::-webkit-scrollbar-track {
                    background: rgba(163, 177, 198, 0.25) !important;
                    border-radius: 10px;
                    border: 1px solid rgba(163, 177, 198, 0.1);
                }
                .coach-board-area::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%) !important;
                    border-radius: 10px;
                    border: 3px solid var(--bg-primary) !important;
                    box-shadow: inset 1px 1px 2px rgba(255, 255, 255, 0.4), 0 2px 6px rgba(37, 99, 235, 0.45) !important;
                }
                .coach-board-area::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%) !important;
                }
                .coach-right-panel { width: 340px; flex-shrink: 0; height: 100%; display: flex; flex-direction: column; gap: 8px; overflow: visible; box-sizing: border-box; }
                .coach-tab-content { flex: 1; overflow: visible; display: flex; flex-direction: column; min-height: 0; }
                .video-resize-handle { position: absolute; background: transparent; transition: background 0.15s ease, box-shadow 0.15s ease; z-index: 1000; }
                .video-resize-handle:hover { background: rgba(59, 130, 246, 0.4) !important; box-shadow: 0 0 6px rgba(59, 130, 246, 0.8); }
                @media (max-width: 1100px) {
                    .coach-classroom { flex-direction: column; height: auto; overflow: visible; }
                    .coach-board-area { width: 100%; height: auto; overflow: visible; }
                    .coach-right-panel { width: 100%; height: 350px; overflow: hidden; margin-top: 10px; }
                }
            `}</style>

            <EndClassModal show={showEndClassModal} onClose={() => setShowEndClassModal(false)} onConfirm={confirmEndClass} classesList={classesList} classId={classToEnd} usersMap={usersMap} />
            <StudentActionModal selectedStudentAction={selectedStudentAction} setSelectedStudentAction={setSelectedStudentAction} usersMap={usersMap} activeClass={activeClass} handleBoardControlChange={handleBoardControlChange} handleAssignQuickHW={handleAssignQuickHW} />
            <SaveLessonModal 
                show={showSaveLessonModal} 
                onClose={() => setShowSaveLessonModal(false)} 
                onConfirm={handleSaveLesson} 
                defaultFolderId={'private_' + user?.email?.toLowerCase()} 
                foldersList={[
                    { id: 'private_' + user?.email?.toLowerCase(), name: '🔒 My Private Workspace' },
                    ...coachFolders
                ]} 
            />
            <LaunchQuizModal show={showLaunchQuizModal} onClose={() => setShowLaunchQuizModal(false)} onConfirm={handleConfirmLaunchQuiz} currentFen={activeClass?.currentFen} />

            {gameToCancel && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.82)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: 'var(--bg-primary)', padding: '24px', borderRadius: '20px',
                        maxWidth: '380px', width: '100%', border: '1px solid rgba(239, 68, 68, 0.4)',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 30px rgba(239, 68, 68, 0.2)', textAlign: 'center',
                        display: 'flex', flexDirection: 'column', gap: '16px'
                    }}>
                        <div style={{ fontSize: '40px' }}>🛑</div>
                        <div>
                            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: '900' }}>Cancel Match?</h3>
                            <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.4' }}>
                                Are you sure you want to stop and cancel this match?
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setGameToCancel(null)}
                                style={{
                                    flex: 1, padding: '10px 14px', background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)',
                                    borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px'
                                }}
                            >
                                Keep Game
                            </button>
                            <button
                                onClick={confirmCancelActiveGame}
                                style={{
                                    flex: 1, padding: '10px 14px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                    color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                                }}
                            >
                                Yes, Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── TOP BAR ── */}
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-primary)', padding: '6px 14px', borderRadius: '8px', marginBottom: '8px', boxShadow: 'var(--neu-shadow)', height: '38px', boxSizing: 'border-box', overflowX: 'auto', flexShrink: 0 }}>
                <div style={{color: '#ef4444', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', whiteSpace: 'nowrap' }}><span style={{animation: 'pulse 1.5s infinite' }}>🔴</span> LIVE</div>
                <div style={{width: '1px', height: '12px', background: 'rgba(163, 177, 198, 0.4)', flexShrink: 0 }}></div>
                <div style={{color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{activeClass.topic}</div>
                <div style={{width: '1px', height: '12px', background: 'rgba(163, 177, 198, 0.4)', flexShrink: 0 }}></div>
                <div style={{color: 'var(--text-secondary)', fontSize: '11px', whiteSpace: 'nowrap' }}>👥 {activeClass.students?.length || 0} students</div>
                <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isEditingLink ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input type="url" value={tempLink} onChange={e => setTempLink(e.target.value)} placeholder="Paste new link..." style={{padding: '3px 7px', borderRadius: '4px', border: '1px solid #3b82f6', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '11px', width: '120px', outline: 'none' }} />
                            <button onClick={handleUpdateMeetLink} style={{background: '#10b981', color: 'black', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', padding: '3px 7px', fontWeight: 'bold' }}>Save</button>
                            <button onClick={() => setIsEditingLink(false)} style={{background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', padding: '3px 7px', fontWeight: 'bold' }}>✖</button>
                        </div>
                    ) : (
                        <div style={{display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(59, 130, 246, 0.1)', padding: '2px 7px', borderRadius: '4px', border: '1px solid #3b82f6' }}>
                            {activeClass.meetLink ? (<a href={activeClass.meetLink} target="_blank" rel="noreferrer" style={{color: '#3b82f6', fontSize: '11px', fontWeight: 'bold', textDecoration: 'none', whiteSpace: 'nowrap' }}>🎥 Video Room</a>) : (<span style={{color: '#ef4444', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>No Link</span>)}
                            <button onClick={() => { setTempLink(activeClass.meetLink || ''); setIsEditingLink(true); }} style={{background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '11px' }} title="Change Link">✏️</button>
                        </div>
                    )}
                    <button onClick={() => changeStatus(activeClass.id, 'COMPLETED')} style={{padding: '0 10px', height: '24px', display: 'flex', alignItems: 'center', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}>⏹ End Class</button>
                </div>
            </div>

            {/* ── MAIN CLASSROOM ── */}
            <div className="coach-classroom">

                {/* LEFT: BOARD + BOARD TOOLS */}
                <div className="coach-board-area">
                    {currentMode === 'PRACTICE' && (
                        <>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexShrink: 0, alignItems: 'center' }}>
                            <button
                                onClick={() => setShowSoloGrid(false)}
                                style={{
                                    padding: '8px 16px',
                                    background: !showSoloGrid ? '#3b82f6' : 'var(--bg-primary)',
                                    color: !showSoloGrid ? 'white' : 'var(--text-primary)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: 'var(--neu-shadow)',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                👨‍🏫 Master Board
                            </button>
                            <button
                                onClick={() => setShowSoloGrid(true)}
                                style={{
                                    padding: '8px 16px',
                                    background: showSoloGrid ? '#3b82f6' : 'var(--bg-primary)',
                                    color: showSoloGrid ? 'white' : 'var(--text-primary)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: 'var(--neu-shadow)',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                ♟️ Student Solo Boards ({Object.values(activeClass.individualBoards || {}).filter(b => b.active).length} Active)
                            </button>
                            {!showSoloGrid && (
                                <button
                                    onClick={handleLaunchCurrentAsQuiz}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        boxShadow: 'var(--neu-shadow)',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginLeft: 'auto'
                                    }}
                                    title="Launch current position on Master Board as live quiz for students"
                                >
                                    🎯 Quiz This Position
                                </button>
                            )}
                        </div>
                    </>
                    )}

                    {currentMode === 'QUIZ' && coachQuizViewMode === 'BOARD' && (
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                            border: '1.5px solid #10b981',
                            borderRadius: '10px',
                            padding: '10px 14px',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '18px' }}>🎯</span>
                                <div>
                                    <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '13px' }}>
                                        Quiz Active for Students ({activeClass.quizData?.question || 'Find the best move'})
                                    </div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                                        You are on Master Board. Students are solving on their boards.
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    onClick={() => setCoachQuizViewMode('MONITOR')} 
                                    style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
                                >
                                    🎯 Return to Quiz Monitor
                                </button>
                                <button 
                                    onClick={handleEndQuiz} 
                                    style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
                                >
                                    🛑 End Puzzle & Award XP
                                </button>
                            </div>
                        </div>
                    )}

                    {currentMode === 'ARCADE_MONITOR' ? (
                        <ArcadeMonitor activeClass={activeClass} usersMap={usersMap} stopArcade={() => switchMode('PRACTICE')} />
                    ) : (currentMode === 'QUIZ' && coachQuizViewMode === 'MONITOR') ? (
                        <div style={{flex: 1, background: 'var(--bg-primary)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexShrink: 0 }}>
                                <span style={{color: '#6ee7b7', fontWeight: 'bold' }}>🎯 Quiz Monitor</span>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handleEndQuiz} style={{padding: '6px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>🛑 End Puzzle & Award XP</button>
                                    <button onClick={() => setCoachQuizViewMode('BOARD')} style={{padding: '6px 14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>👁️ Switch to Master Board</button>
                                </div>
                            </div>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', overflowY: 'auto', flex: 1 }}>
                                {activeClass.students?.map(email => {
                                    const safeEmail = email.replace(/\./g, '_');
                                    const isOnline = activeClass.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
                                    const sFen = activeClass.studentFens?.[safeEmail] || activeClass.quizData?.content || activeClass.currentFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
                                    const solved = activeClass.quizData?.solvedBy?.includes(email);
                                    const attempts = activeClass.quizAttempts?.[safeEmail] || [];
                                    return (
                                        <div key={email} style={{background: 'var(--bg-primary)', padding: '10px', borderRadius: '8px', border: solved ? '2px solid #10b981' : '1px solid rgba(163, 177, 198, 0.4)', opacity: isOnline ? 1 : 0.6 }}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <div onClick={() => setSelectedStudentAction(email)} style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' }}>{usersMap[email] || email.split('@')[0]}</div>
                                                <div>{solved ? '✅' : '⏳'}</div>
                                            </div>
                                            <div style={{width: '100%', pointerEvents: 'none' }}><MiniPreviewBoard fen={sFen} /></div>
                                            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '6px', maxHeight: '40px', overflowY: 'auto' }}>
                                                {attempts.length === 0 ? (
                                                    <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '9px' }}>No attempts</span>
                                                ) : (
                                                    attempts.map((att, idx) => (
                                                        <span key={idx} style={{ 
                                                            fontSize: '9px', 
                                                            padding: '2px 4px', 
                                                            borderRadius: '4px', 
                                                            background: 'var(--bg-secondary)', 
                                                            color: att.correct ? '#10b981' : '#ef4444',
                                                            border: att.correct ? '1px solid #10b981' : '1px solid #ef4444',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {att.move} {att.correct ? '✓' : '✗'}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : currentMode === 'MINIGAME' ? (
                        <div style={{flex: 1, background: 'var(--bg-primary)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(245, 158, 11, 0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexShrink: 0 }}>
                                <span style={{color: '#fcd34d', fontWeight: 'bold' }}>🍎 Basics Monitor</span>
                                <button onClick={() => switchMode('PRACTICE')} style={{padding: '6px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>⬅️ Return to Board</button>
                            </div>
                            
                            {/* Piece Selector and Reset Progress Controls */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', padding: '10px', background: 'rgba(245, 158, 11, 0.08)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 'bold' }}>🍎 Target Basics Piece:</span>
                                <select 
                                    value={activeClass.basicsPiece || 'Rook'} 
                                    onChange={async (e) => {
                                        const p = e.target.value;
                                        await updateDoc(doc(db, "classes", activeClass.id), { 
                                            basicsPiece: p,
                                            miniGameProgress: {} // Reset progress for new piece
                                        });
                                        toast.success(`Assigned ${p} Basics to students! 🍏`);
                                    }} 
                                    style={{
                                        padding: '5px 10px', 
                                        borderRadius: '6px', 
                                        background: 'var(--bg-primary)', 
                                        color: 'var(--text-primary)', 
                                        border: '1px solid rgba(163, 177, 198, 0.4)',
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="Rook">🏰 Rook Basics</option>
                                    <option value="Bishop">📐 Bishop Basics</option>
                                    <option value="Queen">👑 Queen Basics</option>
                                    <option value="Knight">🐴 Knight Basics</option>
                                    <option value="King">🤴 King Basics</option>
                                    <option value="Pawn">♟️ Pawn Basics</option>
                                </select>
                                
                                <button 
                                    onClick={async () => {
                                        await updateDoc(doc(db, "classes", activeClass.id), { miniGameProgress: {} });
                                        toast.success("Restarted game progress for all students! 🍏");
                                    }}
                                    style={{
                                        padding: '5px 10px',
                                        background: 'var(--bg-primary)',
                                        border: '1.5px solid #f59e0b',
                                        color: '#f59e0b',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '11px',
                                        boxShadow: 'var(--neu-shadow)'
                                    }}
                                >
                                    🔄 Restart Game
                                </button>
                            </div>

                            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                {/* 🏆 Basics Leaderboard */}
                                <div style={{ 
                                    width: '220px', 
                                    background: 'rgba(0,0,0,0.15)', 
                                    border: '1px solid rgba(245, 158, 11, 0.2)', 
                                    borderRadius: '12px', 
                                    padding: '12px', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '8px', 
                                    flexShrink: 0 
                                }}>
                                    <div style={{ fontWeight: 'bold', color: '#f59e0b', fontSize: '12px', borderBottom: '1px solid rgba(245, 158, 11, 0.2)', paddingBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        🏆 Live Leaderboard
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', overflowY: 'auto', flex: 1 }}>
                                        {(() => {
                                            const assignedPiece = activeClass.basicsPiece || 'Rook';
                                            const chapter = HUNGRY_CHAPTERS.find(c => c.id === assignedPiece) || HUNGRY_CHAPTERS[0];
                                            const sorted = [...(activeClass.students || [])].sort((a, b) => {
                                                const sa = a.replace(/\./g, '_');
                                                const sb = b.replace(/\./g, '_');
                                                const pa = activeClass.miniGameProgress?.[sa] || { level: 1, applesLeft: 99, wrongAttempts: 0 };
                                                const pb = activeClass.miniGameProgress?.[sb] || { level: 1, applesLeft: 99, wrongAttempts: 0 };
                                                
                                                const compA = pa.applesLeft === 0 && pa.level === chapter.levels.length;
                                                const compB = pb.applesLeft === 0 && pb.level === chapter.levels.length;
                                                
                                                if (compA && !compB) return -1;
                                                if (!compA && compB) return 1;
                                                if (pa.level !== pb.level) return pb.level - pa.level;
                                                if (pa.applesLeft !== pb.applesLeft) return pa.applesLeft - pb.applesLeft;
                                                return pa.wrongAttempts - pb.wrongAttempts;
                                            });

                                            return sorted.map((email, rank) => {
                                                const safeEmail = email.replace(/\./g, '_');
                                                const prog = activeClass.miniGameProgress?.[safeEmail];
                                                const isCompleted = prog ? prog.applesLeft === 0 && prog.level === chapter.levels.length : false;
                                                
                                                let badge = "🥈";
                                                if (rank === 0) badge = "🥇";
                                                else if (rank === 2) badge = "🥉";
                                                else if (rank > 2) badge = "👤";

                                                return (
                                                    <div key={email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: isCompleted ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)', borderRadius: '6px', fontSize: '11px', border: isCompleted ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid transparent' }}>
                                                        <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>
                                                            <span>{badge}</span>
                                                            <span style={{ color: 'var(--text-primary)' }}>{usersMap[email] || email.split('@')[0]}</span>
                                                        </span>
                                                        <span style={{ color: isCompleted ? '#10b981' : '#f59e0b', fontWeight: 'bold' }}>
                                                            {isCompleted ? 'Finished!' : `Lvl ${prog?.level || 1}`}
                                                        </span>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>

                                {/* Student Boards Grid */}
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', overflowY: 'auto', flex: 1 }}>
                                    {activeClass.students?.map(email => {
                                        const safeEmail = email.replace(/\./g, '_');
                                        const isOnline = activeClass.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
                                        const assignedPiece = activeClass.basicsPiece || 'Rook';
                                        const chapter = HUNGRY_CHAPTERS.find(c => c.id === assignedPiece) || HUNGRY_CHAPTERS[0];
                                        const firstLevel = chapter.levels[0];
                                        
                                        const progress = activeClass.miniGameProgress?.[safeEmail] || { 
                                            level: 1, 
                                            applesLeft: firstLevel.apples.length, 
                                            piece: chapter.piece, 
                                            piecePos: firstLevel.start, 
                                            apples: firstLevel.apples,
                                            wrongAttempts: 0 
                                        };
                                        const isCompleted = progress.applesLeft === 0 && progress.level === chapter.levels.length;
                                        
                                        return (
                                            <div key={email} style={{background: 'var(--bg-primary)', padding: '10px', borderRadius: '8px', border: isCompleted ? '2px solid #10b981' : '1px solid rgba(163, 177, 198, 0.4)', opacity: isOnline ? 1 : 0.6 }}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '5px' }}>
                                                    <span style={{fontWeight: 'bold', color: 'var(--text-primary)' }}>{usersMap[email] || email.split('@')[0]}</span>
                                                    <span>{isCompleted ? '✅' : `🍎 ${progress.applesLeft}`}</span>
                                                </div>
                                                <div style={{width: '100%', pointerEvents: 'none' }}><MiniBasicsBoard piece={progress.piece} piecePos={progress.piecePos} apples={progress.apples} /></div>
                                                {progress.wrongAttempts >= 2 && <div style={{fontSize: '10px', color: '#ef4444', marginTop: '3px' }}>⚠️ Need Help</div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : currentMode === 'PAIRING' ? (
                        <div style={{flex: 1, background: 'var(--bg-primary)', padding: '16px', borderRadius: '12px', border: '2px solid #ec4899', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <h3 style={{margin: '0 0 12px 0', color: '#ec4899', textAlign: 'center', flexShrink: 0 }}>⚔️ Classroom Arena — LIVE!</h3>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', overflowY: 'auto', flex: 1 }}>
                                {Object.keys(activeClass.activeGames || {}).length === 0 ? <p style={{color: 'var(--text-secondary)'}}>No active games.</p> :
                                Object.entries(activeClass.activeGames).map(([gId, g]) => (
                                    <div key={gId} style={{background: 'var(--bg-primary)', padding: '10px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.3)', width: '180px', display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: 'var(--panel-shadow)' }}>
                                        <div style={{fontSize: '11px', fontWeight: 'bold', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⚫ {resolvePlayerName(g.black)}</div>
                                        <div style={{width: '100%', pointerEvents: 'none' }}><MiniPreviewBoard fen={g.fen} /></div>
                                        <div style={{fontSize: '11px', fontWeight: 'bold', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⚪ {resolvePlayerName(g.white)}</div>
                                        <div style={{ display: 'flex', gap: '5px', marginTop: '4px' }}>
                                            <button 
                                                onClick={() => setPlayingArenaGameId(gId)}
                                                style={{
                                                    flex: 1,
                                                    padding: '5px',
                                                    background: (g.white === user.email || g.black === user.email) ? '#10b981' : 'rgba(59,130,246,0.15)',
                                                    color: (g.white === user.email || g.black === user.email) ? 'black' : '#3b82f6',
                                                    border: (g.white === user.email || g.black === user.email) ? 'none' : '1px solid #3b82f6',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '9px'
                                                }}
                                            >
                                                {(g.white === user.email || g.black === user.email) ? '🎮 Play' : '👁️ Watch'}
                                            </button>
                                            <button 
                                                onClick={() => cancelActiveGame(gId)}
                                                style={{
                                                    padding: '5px 8px',
                                                    background: 'transparent',
                                                    border: '1px solid #ef4444',
                                                    color: '#ef4444',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '9px'
                                                }}
                                                title="Stop Match"
                                            >
                                                ❌
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : showSoloGrid ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', minHeight: 0 }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.08)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)', flexShrink: 0 }}>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚔️ Simuls Control:</span>
                                <button 
                                    onClick={handleStartAllSoloPlay}
                                    style={{ padding: '4px 10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)' }}
                                >
                                    🎮 Start Solo for All
                                </button>
                                <button 
                                    onClick={handleStopAllSoloPlay}
                                    style={{ padding: '4px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)' }}
                                >
                                    🛑 Stop Solo for All
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: '15px', overflowY: 'auto', flex: 1, padding: '5px' }}>
                                {activeClass.students?.map(email => {
                                    const safeEmail = email.replace(/\./g, '_');
                                    const isOnline = activeClass.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
                                    const isSoloActive = activeClass.individualBoards?.[safeEmail]?.active;
                                    const status = activeClass.studentStatus?.[safeEmail];
                                    const isRaisedHand = status === 'RAISED_HAND';
                                    const isDone = status === 'DONE';
                                    
                                    const cardBorder = isRaisedHand 
                                        ? '3.5px solid #f59e0b' 
                                        : isDone 
                                        ? '3.5px solid #10b981' 
                                        : isSoloActive 
                                        ? '2.5px solid #3b82f6' 
                                        : '1px dashed rgba(163, 177, 198, 0.4)';
                                        
                                    return (
                                        <div key={email} style={{
                                            background: 'var(--bg-secondary)',
                                            padding: '12px',
                                            borderRadius: '12px',
                                            border: cardBorder,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            boxShadow: isRaisedHand ? '0 0 15px rgba(245, 158, 11, 0.4)' : 'var(--neu-shadow)',
                                            alignItems: 'center',
                                            boxSizing: 'border-box',
                                            transform: isRaisedHand ? 'scale(1.01)' : 'scale(1)',
                                            transition: 'all 0.25s'
                                        }}>
                                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
                                                    <span style={{ width: '7px', height: '7px', background: isOnline ? '#10b981' : '#64748b', borderRadius: '50%', flexShrink: 0 }}></span>
                                                    {usersMap[email] || email.split('@')[0]}
                                                    {isRaisedHand && <span style={{ animation: 'wiggle 0.6s infinite', fontSize: '13px' }}>🙋</span>}
                                                    {isDone && <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '12px' }}>✅</span>}
                                                </span>
                                                {isSoloActive && (
                                                    <div style={{ display: 'flex', gap: '4px' }}>
                                                        <button 
                                                            onClick={async () => {
                                                                await updateDoc(doc(db, "classes", activeClass.id), {
                                                                    [`individualBoards.${safeEmail}.fen`]: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                                                                    [`studentFens.${safeEmail}`]: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
                                                                });
                                                                toast.success(`Reset ${usersMap[email] || email.split('@')[0]}'s board!`);
                                                            }}
                                                            style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                                        >
                                                            🔄 Reset
                                                        </button>
                                                        <button 
                                                            onClick={() => handleRevertIndividualBoard(email)} 
                                                            style={{ padding: '2px 6px', background: '#ef4444', border: 'none', color: 'white', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                                        >
                                                            Stop Solo
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {isSoloActive ? (
                                                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                    <ChessBoard 
                                                        role="COACH" 
                                                        classroomId={activeClass.id} 
                                                        isFrozen={false} 
                                                        isIndividualMode={true} 
                                                        individualStudentEmail={email}
                                                        rightPanelWidth={0}
                                                        boardWidth={300}
                                                        isMinimized={true}
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{ width: '100%', height: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '24px' }}>♟️</span>
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Master Board active for this student</span>
                                                    <button 
                                                        onClick={() => handleAssignIndividualBoard(email)}
                                                        style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                                    >
                                                        Start Solo Play
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        /* ── NORMAL PRACTICE MODE: board without empty tools container ── */
                        <ChessBoard role="COACH" classroomId={activeClass.id} isFrozen={false} userEmail={user.email} rightPanelWidth={rightPanelWidth} />
                    )}
                    
                    {/* Visual Scroll Helper for mobile/tablet coaches */}
                    {window.innerWidth <= 1100 && (
                        <div 
                            onClick={() => rightPanelRef.current?.scrollIntoView({ behavior: 'smooth' })}
                            className="coach-scroll-indicator"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                background: 'rgba(59, 130, 246, 0.12)',
                                border: '1px dashed rgba(59, 130, 246, 0.5)',
                                borderRadius: '10px',
                                padding: '12px',
                                color: '#3b82f6',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                cursor: 'pointer',
                                marginTop: '12px',
                                boxShadow: 'var(--neu-shadow)',
                                transition: 'all 0.2s',
                                userSelect: 'none'
                            }}
                        >
                            <span style={{ fontSize: '14px' }}>⬇️</span> Click to scroll down for Video Feed & Class Controls (Moves, Notes, Roster)
                        </div>
                    )}
                </div>

                {/* RIGHT: VIDEO → MODE SLIDER → TABS */}
                <div ref={rightPanelRef} className="coach-right-panel" style={{ width: window.innerWidth > 1100 ? `${rightPanelWidth}px` : '100%', position: 'relative', overflow: 'visible', transition: isDraggingRightPanel ? 'none' : 'width 0.2s ease' }}>
                
                {window.innerWidth > 1100 && (
                    <div 
                        onClick={() => setRightPanelWidth(rightPanelWidth === 0 ? 480 : 0)}
                        style={{
                            position: 'absolute',
                            left: '-24px',
                            top: '20px',
                            width: '24px',
                            height: '40px',
                            background: 'var(--bg-secondary)',
                            border: '1px solid rgba(163, 177, 198, 0.4)',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 1001,
                            boxShadow: '-2px 2px 5px rgba(0,0,0,0.1)',
                            color: 'var(--text-primary)',
                            fontSize: '12px'
                        }}
                        title={rightPanelWidth === 0 ? "Expand Panel" : "Collapse Panel"}
                    >
                        {rightPanelWidth === 0 ? '◀' : '▶'}
                    </div>
                )}
                    
                    {/* Vertical Drag Handle */}
                    {window.innerWidth > 1100 && rightPanelWidth > 0 && (
                        <div 
                            onPointerDown={startRightPanelResize}
                            style={{
                                position: 'absolute',
                                left: '-5px',
                                top: 0,
                                bottom: 0,
                                width: '10px',
                                cursor: 'col-resize',
                                zIndex: 1000,
                                background: 'transparent',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.4)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        />
                    )}

                    <div style={{
                        display: rightPanelWidth === 0 ? 'none' : 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden'
                    }}>
                        {/* Video Feed */}
                        <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.3)', padding: '10px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '7px', flexShrink: 0, position: 'relative', paddingBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                <span style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '12px' }}>🎥 Video Feed</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    {/* Size buttons */}
                                    {[
                                        { label: '🔇', val: '0px', title: 'Hide' },
                                        { label: '📱', val: '160px', title: 'Small' },
                                        { label: '💻', val: '240px', title: 'Medium' },
                                        { label: '🖥️', val: '360px', title: 'Large' }
                                    ].map(p => (
                                        <button key={p.val} onClick={() => setVideoHeight(p.val)} title={p.title} style={{ padding: '3px 7px', borderRadius: '5px', fontSize: '11px', border: 'none', cursor: 'pointer', fontWeight: 'bold', background: videoHeight === p.val ? '#3b82f6' : 'var(--bg-primary)', color: videoHeight === p.val ? 'white' : 'var(--text-secondary)', boxShadow: videoHeight === p.val ? '0 2px 5px rgba(37,99,235,0.3)' : 'var(--neu-shadow)', outline: 'none' }}>{p.label}</button>
                                    ))}
                                    <button
                                        onClick={async () => {
                                            const newState = !activeClass.videoActive;
                                            await updateDoc(doc(db, "classes", activeClass.id), { videoActive: newState });
                                            toast.success(newState ? "🎥 Video started!" : "🛑 Video stopped.");
                                        }}
                                        style={{ padding: '3px 8px', borderRadius: '5px', fontSize: '10px', border: 'none', cursor: 'pointer', fontWeight: 'bold', background: activeClass.videoActive ? '#ef4444' : '#10b981', color: 'white', outline: 'none' }}
                                    >
                                        {activeClass.videoActive ? '⏹' : '▶'}
                                    </button>
                                </div>
                            </div>
                            
                            <div 
                                ref={videoContainerRef}
                                style={{ 
                                    height: videoHeight !== '0px' ? videoHeight : 'auto', 
                                    display: videoHeight === '0px' ? 'none' : 'block', 
                                    width: '100%',
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}
                            >
                                {activeClass.videoActive ? (
                                    <LiveVideo roomName={activeClass.id} userName={user.email} role="COACH" height="100%" />
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.12)', borderRadius: '7px', color: 'var(--text-secondary)', fontSize: '11px', fontStyle: 'italic', border: '1px dashed rgba(163, 177, 198, 0.4)', boxSizing: 'border-box', minHeight: '120px' }}>
                                        Click ▶ to start the video call
                                    </div>
                                )}
                            </div>

                            {/* 4 Edge Drag Handles for Video Call Container */}
                            {videoHeight !== '0px' && (
                                <>
                                    <div onPointerDown={startVideoResizeFromEdge('top')} className="video-resize-handle" style={{ top: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize' }} title="Drag top edge to resize video height" />
                                    <div onPointerDown={startVideoResizeFromEdge('bottom')} className="video-resize-handle" style={{ bottom: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize' }} title="Drag bottom edge to resize video height" />
                                    <div onPointerDown={startVideoResizeFromEdge('left')} className="video-resize-handle" style={{ left: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize' }} title="Drag left edge to resize panel width" />
                                    <div onPointerDown={startVideoResizeFromEdge('right')} className="video-resize-handle" style={{ right: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize' }} title="Drag right edge to resize panel width" />
                                </>
                            )}
                        </div>

                        {/* Mode Slider */}
                        <div style={{flexShrink: 0 }}>
                            {renderModeSlider()}
                        </div>

                        {/* Tab bar */}
                        <div style={{display: 'flex', background: 'var(--bg-primary)', borderRadius: '8px 8px 0 0', border: '1px solid rgba(163, 177, 198, 0.4)', borderBottom: 'none', flexShrink: 0, overflow: 'hidden' }}>
                            {[
                                { id: 'moves', label: '📊 Moves', color: '#2563eb', activeColor: '#1e40af' },
                                { id: 'notes', label: '📝 Notes', color: '#10b981', activeColor: '#065f46' },
                                { id: 'leaderboard', label: '🏆 Ranks', color: '#f59e0b', activeColor: '#b45309' },
                                { id: 'tactics', label: '🧩 Tactics', color: '#f59e0b', activeColor: '#b45309' },
                                { id: 'curriculum', label: '🎓 Course', color: '#8b5cf6', activeColor: '#6d28d9' },
                                { id: 'library', label: '📚 Lib', color: '#f59e0b', activeColor: '#9a3412' },
                                { id: 'diary', label: '📓 Diary', color: '#ec4899', activeColor: '#be185d' },
                                { id: 'participants', label: '👥 Roster', color: '#3b82f6', activeColor: '#1d4ed8' }
                            ].map(tab => (
                                <button key={tab.id} onClick={() => setRightTab(tab.id)} style={{ flex: 1, padding: '8px 1px', background: 'transparent', color: rightTab === tab.id ? tab.activeColor : 'var(--text-secondary)', border: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: '9px', borderBottom: rightTab === tab.id ? `3px solid ${tab.color}` : '3px solid transparent', transition: 'all 0.15s' }}>{tab.label}</button>
                            ))}
                        </div>

                        {/* Tab content */}
                        <div className="coach-tab-content" style={{ background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                            {renderTabContent()}
                        </div>
                    </div>

            {spectatingStudentEmail && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    width: isIndividualMinimized ? '260px' : spectatorCardWidth + 'px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '16px',
                    border: '2.5px solid #2563eb',
                    padding: isIndividualMinimized ? '10px' : '16px',
                    boxShadow: isDragging || isResizing 
                        ? '0 25px 55px rgba(0,0,0,0.4), 0 0 15px rgba(59, 130, 246, 0.5)' 
                        : (isIndividualMinimized ? '0 10px 25px rgba(0,0,0,0.25)' : '0 15px 35px rgba(0,0,0,0.3)'),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isIndividualMinimized ? '6px' : '12px',
                    zIndex: 99999,
                    boxSizing: 'border-box',
                    maxHeight: isIndividualMinimized ? 'auto' : '85vh',
                    overflowY: isIndividualMinimized ? 'visible' : 'auto',
                    transform: `translate(${spectatorCardPosition.x}px, ${spectatorCardPosition.y}px)`,
                    touchAction: 'none',
                    opacity: isDragging ? 0.85 : 1,
                    scale: isDragging ? '1.02' : '1',
                    borderColor: isDragging || isResizing ? '#3b82f6' : '#2563eb',
                    transition: (isDragging || isResizing) ? 'none' : 'width 0.2s, padding 0.2s, gap 0.2s, opacity 0.2s, scale 0.2s, border-color 0.2s, box-shadow 0.2s'
                }}>
                    {/* 8-Directional Resize Handles */}
                    {!isIndividualMinimized && (
                        <>
                            {/* Left edge */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('left')}
                                style={{ position: 'absolute', left: '-4px', top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 1000, background: 'transparent' }}
                                title="Drag edge to resize window"
                            />
                            {/* Right edge */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('right')}
                                style={{ position: 'absolute', right: '-4px', top: 0, bottom: 0, width: '8px', cursor: 'ew-resize', zIndex: 1000, background: 'transparent' }}
                                title="Drag edge to resize window"
                            />
                            {/* Top edge */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('top')}
                                style={{ position: 'absolute', top: '-4px', left: 0, right: 0, height: '8px', cursor: 'ns-resize', zIndex: 1000, background: 'transparent' }}
                                title="Drag edge to resize window"
                            />
                            {/* Bottom edge */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('bottom')}
                                style={{ position: 'absolute', bottom: '-4px', left: 0, right: 0, height: '8px', cursor: 'ns-resize', zIndex: 1000, background: 'transparent' }}
                                title="Drag edge to resize window"
                            />
                            {/* Top-Left Corner */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('top-left')}
                                style={{ position: 'absolute', left: '-6px', top: '-6px', width: '12px', height: '12px', cursor: 'nwse-resize', zIndex: 1001, background: 'transparent' }}
                                title="Drag corner to resize window"
                            />
                            {/* Top-Right Corner */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('top-right')}
                                style={{ position: 'absolute', right: '-6px', top: '-6px', width: '12px', height: '12px', cursor: 'nesw-resize', zIndex: 1001, background: 'transparent' }}
                                title="Drag corner to resize window"
                            />
                            {/* Bottom-Left Corner */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('bottom-left')}
                                style={{ position: 'absolute', left: '-6px', bottom: '-6px', width: '12px', height: '12px', cursor: 'nesw-resize', zIndex: 1001, background: 'transparent' }}
                                title="Drag corner to resize window"
                            />
                            {/* Bottom-Right Corner */}
                            <div 
                                onPointerDown={startResizeSpectatorCard('bottom-right')}
                                style={{ position: 'absolute', right: '-6px', bottom: '-6px', width: '12px', height: '12px', cursor: 'nwse-resize', zIndex: 1001, background: 'transparent' }}
                                title="Drag corner to resize window"
                            />
                        </>
                    )}

                    <div 
                        onPointerDown={startDragSpectatorCard}
                        style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
                            paddingBottom: isIndividualMinimized ? '6px' : '10px', 
                            marginBottom: isIndividualMinimized ? '4px' : '5px',
                            cursor: 'move',
                            userSelect: 'none'
                        }}
                    >
                        <span style={{ 
                            fontSize: isIndividualMinimized ? '11px' : '14px', 
                            fontWeight: 'bold', 
                            color: 'var(--text-primary)', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            maxWidth: isIndividualMinimized ? '160px' : '75%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <span style={{ color: 'rgba(255, 255, 255, 0.3)', letterSpacing: '-1px', fontWeight: 'normal', fontSize: '12px' }}>⋮⋮</span>
                            {isIndividualMinimized ? `👤 Solo: ${usersMap[spectatingStudentEmail] || spectatingStudentEmail.split('@')[0]}` : `♟️ Controlling: ${usersMap[spectatingStudentEmail] || spectatingStudentEmail.split('@')[0]}`}
                        </span>
                        
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button 
                                onClick={() => setIsIndividualMinimized(!isIndividualMinimized)}
                                onPointerDown={(e) => e.stopPropagation()}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: 'var(--text-primary)',
                                    width: '26px',
                                    height: '26px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontSize: '12px',
                                    outline: 'none',
                                    padding: 0
                                }}
                                title={isIndividualMinimized ? "Maximize" : "Minimize"}
                            >
                                {isIndividualMinimized ? (
                                    <div style={{ width: '8px', height: '8px', border: '1.5px solid currentColor', borderRadius: '1px' }} />
                                ) : (
                                    <div style={{ width: '8px', height: '2px', background: 'currentColor' }} />
                                )}
                            </button>
                            <button 
                                onClick={() => setSpectatingStudentEmail(null)}
                                onPointerDown={(e) => e.stopPropagation()}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: 'var(--text-primary)',
                                    width: '26px',
                                    height: '26px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontSize: '12px',
                                    outline: 'none',
                                    padding: 0
                                }}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                    
                    {!isIndividualMinimized && (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <ChessBoard 
                                role="COACH" 
                                classroomId={activeClass.id} 
                                isFrozen={false} 
                                isIndividualMode={true} 
                                individualStudentEmail={spectatingStudentEmail}
                                rightPanelWidth={0}
                                boardWidth={spectatorCardWidth - 60}
                                isMinimized={false}
                            />
                        </div>
                    )}
                    
                    {!isIndividualMinimized && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                onClick={async () => {
                                    const safeEmail = spectatingStudentEmail.replace(/\./g, '_');
                                    await updateDoc(doc(db, "classes", activeClass.id), {
                                        [`individualBoards.${safeEmail}.fen`]: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                                        [`studentFens.${safeEmail}`]: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
                                    });
                                    toast.success("Board reset to starting position!");
                                }}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    background: 'rgba(255,255,255,0.05)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                🔄 Reset
                            </button>
                            <button 
                                onClick={() => {
                                    handleRevertIndividualBoard(spectatingStudentEmail);
                                    setSpectatingStudentEmail(null);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                 🛑 Stop Solo Mode
                            </button>
                        </div>
                    )}
                </div>
            )}
            </div>

            {playingArenaGameId && activeClass?.activeGames && activeClass.activeGames[playingArenaGameId] && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.65)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '20px',
                        width: '90%',
                        maxWidth: '550px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        position: 'relative'
                    }}>
                        <button 
                            onClick={() => setPlayingArenaGameId(null)}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--text-secondary)',
                                fontSize: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            ✕
                        </button>
                        
                        {(() => {
                            const game = activeClass.activeGames[playingArenaGameId];
                            const isWhite = game.white === user.email;
                            const isBlack = game.black === user.email;
                            const roleToPass = isWhite ? 'WHITE' : (isBlack ? 'BLACK' : 'SPECTATOR');
                            const wLabel = usersMap[game.white.toLowerCase()] || game.white.split('@')[0];
                            const bLabel = usersMap[game.black.toLowerCase()] || game.black.split('@')[0];
                            return (
                                <>
                                    <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>⚔️</span> Arena Board: <span style={{ color: 'var(--accent)' }}>{wLabel} vs {bLabel}</span>
                                        <span style={{ fontSize: '11px', padding: '3px 7px', borderRadius: '4px', background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)' }}>{roleToPass}</span>
                                    </h3>
                                    
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        <ChessBoard 
                                            role={roleToPass} 
                                            classroomId={activeClass.id} 
                                            isFrozen={false} 
                                            userEmail={user.email}
                                            isArenaMode={true} 
                                            arenaGameId={playingArenaGameId}
                                            rightPanelWidth={0}
                                            boardWidth={440}
                                        />
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button 
                                            onClick={() => setPlayingArenaGameId(null)}
                                            style={{
                                                flex: 1,
                                                padding: '8px 12px',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                textAlign: 'center'
                                            }}
                                        >
                                            🛑 Close Board
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
        </div>
    );
}
